/**
 * 타입드 API 클라이언트.
 *
 * 화면 코드가 경로 문자열을 직접 다루지 않게 한다. 서버 계약이 바뀌면 여기 한 곳만
 * 고치면 되고, 무엇보다 **타입이 깨져서** 고칠 곳이 컴파일 시점에 드러난다.
 */
import type {
  CaptionSettings,
  HeaderPlaceSetting,
  HtmlFit,
  HtmlItemFit,
  TemplateMeta,
  TemplatePayload,
  Transition,
} from '@signageone/core';
import type { Transport, TransportResponse } from './transport.js';
import type { PendingPairing } from '../screens/pendingPairings.js';

export interface ListResponse<T> {
  items: T[];
  next_cursor: string | null;
}

/** 한 번에 받는 수(서버 상한)와, 끝까지 따라가 받을 때의 상한. */
export const LIST_PAGE = 200;
export const LIST_CAP = 5000;

/**
 * 쪽을 끝까지 따라가 모두 받는다.
 *
 * 콘솔의 목록·고르기는 받은 것 안에서 정렬·검색한다. 한 쪽(기본 50개)만 받으면 51번째부터는
 * 어느 순서로도, 어떤 검색어로도 나오지 않는다 — 플레이리스트 편집기에서 오래된 콘텐츠를 담을 수
 * 없었던 까닭이다. 상한(`cap`)을 넘으면 거기서 멈추고 **`next_cursor` 를 남겨 잘렸다고 알린다.**
 */
export async function collectPages<T>(
  page: (cursor: string | undefined) => Promise<ListResponse<T>>,
  cap: number = LIST_CAP,
): Promise<ListResponse<T>> {
  const items: T[] = [];
  let cursor: string | undefined;
  for (;;) {
    const result = await page(cursor);
    items.push(...result.items);
    if (!result.next_cursor) return { items, next_cursor: null };
    if (items.length >= cap) return { items, next_cursor: result.next_cursor };
    // 같은 커서가 돌아오면 서버가 다음 쪽을 모르는 것이다(옛 서버) — 같은 쪽을 끝없이 받지 않는다.
    if (result.next_cursor === cursor) return { items, next_cursor: result.next_cursor };
    cursor = result.next_cursor;
  }
}

/** 이 콘텐츠가 들어 있는 편성. 보관된 편성은 세지 않는다. */
export interface AssetUsage {
  id: string;
  name: string;
  /**
   * 플레이리스트인가, 콘텐츠를 직접 가리키는 스케줄 규칙인가. 옛 서버 응답에는 없다(플레이리스트로 본다).
   */
  kind?: 'playlist' | 'schedule';
}

/**
 * 콘텐츠 하나의 배포 현황 — 서버가 센다(`GET /assets/:id?include=reach`).
 *
 * 예전에는 콘솔이 다섯 목록을 받아 셌다(옛 `content-usage.ts`). 규칙은 그대로 서버로 옮겼다 — 화면 배정·
 * 그룹에서 물려받기·스케줄의 기본과 규칙·게재 중인 캠페인. 바로 보내기·긴급은 세지 않는다.
 */
export interface ContentReach {
  routes: ContentReachRoute[];
  /** 경로를 합쳐 중복을 뺀 화면 수. */
  displays: number;
}

export interface ContentReachRoute {
  /** 목록 키 — 같은 이름의 플레이리스트·스케줄이 있어도 겹치지 않게 종류를 붙인다. */
  key: string;
  kind: 'playlist' | 'schedule';
  id: string;
  name: string;
  /** 이 경로로 콘텐츠가 나가는 화면. */
  displays: Array<{ id: string; name: string }>;
  /** 어떻게 닿는가 — "스케줄 ‘본관’" · "캠페인 ‘추석’". 직접 배정은 적지 않는다. */
  via: string[];
}

// ── 변경 이력
export type HistoryType = 'playlist' | 'schedule' | 'campaign' | 'template';

export interface HistoryEntry {
  version: number;
  /** 이 판을 만든 것 — 처음 만듦·고침·되돌림. */
  reason: 'create' | 'update' | 'restore';
  /** 만든 사람. 공급사 계정의 판이면 비어 있다(고객사에는 가린다). */
  actor: string;
  created_at: string;
  summary: string;
}

// ── 환경 설정 (고객사)
export type IdleMode = 'message' | 'black' | 'logo';

export interface HoursRule {
  days: number[];
  start: string;
  end: string;
}

export interface TenantSettings {
  /** 웹 콘텐츠로 띄울 수 있는 사내 주소 — 호스트·`*.도메인`·IPv4(대역)·IPv6. */
  web_allowlist: string[];
  brand: {
    name: string;
    logo_asset_id: string | null;
    /** 로고 미리보기 주소(단명 서명). 로고가 없거나 보관됐으면 null. */
    logo_preview_url: string | null;
    accent: string | null;
    /** 올린 사내 글꼴. `preview_url` 은 콘솔 미리보기용 단명 서명 주소. */
    font: { checksum: string; size: number; mime: string; filename: string; preview_url: string } | null;
    /** 머리글의 장소 글 — 화면 이름·위치·직접 입력·표시 안 함(2026-11-03 서버부터, 옛 서버에는 없다). */
    place?: HeaderPlaceSetting;
  };
  idle: { mode: IdleMode };
  /** `panel_off` — 운영 시간 밖에는 패널(TV) 전원을 끈다(2026-10-19 서버부터, 옛 서버에는 없다). */
  hours: { enabled: boolean; rules: HoursRule[]; panel_off?: boolean };
  /** 등록할 때 그룹을 고르지 않은 화면이 드는 그룹(2026-10-28 서버부터, 옛 서버에는 없다). */
  new_display_group_id?: string | null;
  /** 재생 중 제목 표시 — 켬·자리·긴 제목(2026-10-28 서버부터, 옛 서버에는 없다). */
  caption?: CaptionSettings;
  /** 올린 HTML 의 바깥 연결 — `offline` 이면 화면·미리보기가 바깥 주소를 부르지 않는다(2026-11-03 서버부터, 옛 서버에는 없다). */
  html?: { offline: boolean };
  /** 저장할 때 그대로 돌려보낸다 — 그사이 남이 저장했으면 409. */
  revision: number;
  updated_by: string;
  updated_at: string | null;
}

export interface TenantSettingsPatch {
  revision: number;
  web_allowlist?: string[];
  brand?: { name?: string; logo_asset_id?: string | null; accent?: string | null; place?: HeaderPlaceSetting };
  idle?: { mode: IdleMode };
  hours?: { enabled: boolean; rules: HoursRule[]; panel_off?: boolean };
  new_display_group_id?: string | null;
  caption?: Partial<CaptionSettings>;
  html?: { offline: boolean };
}

// ── 장애 감시
export type AlertRule =
  | 'offline_ratio'
  | 'display_offline'
  | 'proof_stalled'
  | 'api_error_rate'
  | 'disk_low'
  | 'backup_failed'
  | 'cert_expiring';
export type AlertChannel = 'email' | 'messenger' | 'sms';

export interface AlertNotificationView {
  channel: AlertChannel;
  event: 'firing' | 'resolved' | 'test';
  at: string;
  ok: boolean;
  error: string;
}

export interface AlertRecord {
  id: string;
  rule: AlertRule;
  rule_label: string;
  status: 'firing' | 'resolved';
  summary: string;
  value: Record<string, unknown>;
  started_at: string;
  resolved_at: string | null;
  last_seen_at: string;
  notifications: AlertNotificationView[];
}

export interface AlertList {
  items: AlertRecord[];
  monitor: {
    interval_sec: number;
    thresholds: {
      offline_ratio: number;
      offline_minutes: number;
      proof_stall_minutes: number;
      api_error_rate: number;
      /** 아래 넷은 2026-10-19 서버부터 — 옛 서버는 주지 않는다. 0 이면 그 규칙을 끈 것이다. */
      display_offline_minutes?: number;
      disk_free_percent?: number;
      backup_stale_hours?: number;
      cert_days?: number;
    };
  };
}

export interface AlertCheckResult {
  rule: AlertRule;
  active: boolean;
  summary: string;
  /** `renotified` 는 열린 장애에 새로 알릴 것이 생겨 다시 알린 것이다(새로 꺼진 화면). */
  transition: 'fired' | 'resolved' | 'ongoing' | 'renotified' | 'pending' | 'none';
}

/** 알림 채널 설정 — **비밀은 오지 않는다**(`*_set` 로 설정 여부만). 플러그인 '장애 알림 발송'(베타). */
export interface AlertSettingsView {
  email: { enabled: boolean; host: string; port: number; secure: boolean; user: string; password_set: boolean; from: string; to: string[] };
  messenger: { enabled: boolean; format: 'slack' | 'teams' | 'jandi' | 'dooray' | 'generic'; webhook_url_set: boolean };
  sms: {
    enabled: boolean;
    url_set: boolean;
    method: 'POST' | 'GET';
    headers_set: boolean;
    content_type: 'json' | 'form';
    body_template: string;
    to: string[];
  };
  updated_at: string | null;
}

/** 저장할 때 보내는 것. 비밀 칸은 **바꿀 때만** 넣는다 — 빼면 서버가 그대로 둔다. */
export interface AlertSettingsInput {
  email?: Partial<Omit<AlertSettingsView['email'], 'password_set'> & { password: string }>;
  messenger?: Partial<Omit<AlertSettingsView['messenger'], 'webhook_url_set'> & { webhook_url: string }>;
  sms?: Partial<Omit<AlertSettingsView['sms'], 'url_set' | 'headers_set'> & { url: string; headers: string }>;
}

// ── 플러그인 (시스템 역할, 베타)
export interface BackupExternalSettings {
  enabled: boolean;
  /** 서버가 보는 절대 경로. Docker 설치는 `/backup-external` 아래. */
  path: string;
  keep: number;
  /** 매일 이 시각(KST). */
  hour: number;
  include_media: boolean;
  last_scheduled_on: string | null;
}

export interface PathInspection {
  path: string;
  ok: boolean;
  problems: string[];
  in_container: boolean;
  mount_point: string | null;
  free_bytes: number | null;
  files: Array<{ name: string; size: number; modified_at: string }>;
}

export interface PluginRunView {
  id: string;
  plugin: 'backup-external' | 'alert-notify';
  action: 'run' | 'check' | 'test';
  trigger: 'manual' | 'schedule';
  actor: string;
  status: 'running' | 'ok' | 'failed';
  started_at: string;
  finished_at: string | null;
  message: string;
  detail: Record<string, unknown>;
}

export type PluginView =
  | {
      id: 'backup-external';
      name: string;
      settings: BackupExternalSettings;
      environment: { in_container: boolean; suggested_path: string };
    }
  | { id: 'alert-notify'; name: string; settings: AlertSettingsView };

/** 모듈 — 켜고 끄고 단계를 정할 수 있는 기능의 단위. 플러그인 둘에 장애 감시를 더한 셋. */
export type ModuleId = 'backup-external' | 'alert-notify' | 'alert-monitor' | 'hub';

/** 모듈 하나의 네 가지 상태. 넷이 서로 독립이다. */
export interface ModuleFlags {
  /** 동작하는가. 끄면(해제) 예약·발송·주기 점검이 멈추고 설정은 남는다. */
  enabled: boolean;
  beta: boolean;
  /** 고객사 공개 — 관리자도 이 모듈의 상태를 본다. */
  exposed: boolean;
  /** 패키지 기본값으로 싣는다 — 새로 설치한 서버가 이 상태로 시작한다. */
  packaged: boolean;
}

/** 개발자 화면이 보는 모듈 하나. 설명과 "해제하면 무엇이 멈추는가" 는 서버의 모듈 표에서 온다. */
export interface ModuleView extends ModuleFlags {
  id: ModuleId;
  name: string;
  summary: string;
  stops: string;
  /** 이 값이 어디서 왔나 — 이 서버의 결정 · 패키지 기본값 · 제품 기본값. */
  source: 'server' | 'package' | 'product';
  shipped: ModuleFlags;
  package_default: Partial<ModuleFlags> | null;
  last_run: PluginRunView | null;
}

/**
 * 고객사에 **공개한** 모듈 — 관리자도 본다(`read`). 시스템·장애 감시 화면이 "이 기능이 켜져 있고
 * 어제 잘 돌았다" 를 말하는 데 쓴다. 설정도 비밀도 오지 않는다.
 */
export interface ExposedModule {
  id: ModuleId;
  name: string;
  summary: string;
  beta: boolean;
  enabled: boolean;
  last_run: { status: 'running' | 'ok' | 'failed'; message: string; started_at: string } | null;
}

export interface PluginList {
  modules: ModuleView[];
  /** 지금 정한 것을 패키지가 실어 나를 한 줄 + 이 서버가 실제로 받은 줄. */
  module_defaults: { line: string; env: string; unknown: string[] };
  plugins: PluginView[];
  runs: PluginRunView[];
}

/** 기기가 하트비트로 알려 온 상태(0.7.17+). 값마다 모르면 빠진다 — 기기·OS 마다 읽을 수 있는 것이 다르다. */
export interface DeviceHealthView {
  cpu_percent?: number;
  mem_total?: number;
  mem_available?: number;
  temperature_c?: number;
  thermal_status?: string;
  uptime_sec?: number;
  app_uptime_sec?: number;
  model?: string;
  os?: string;
  network?: { kind: string; connected: boolean; ssid?: string | null; wifi_rssi?: number | null; wifi_mhz?: number | null; ipv4?: string | null };
  /** 받은 시각. */
  at: string;
}

/** 받은 기기 로그 — 본문은 `displayLogUrl` 로 내려받는다. */
export interface DeviceLogEntry {
  id: string;
  request_id: string;
  received_at: string;
  size: number;
  app_version: string | null;
}

export interface Asset {
  id: string;
  title: string;
  type: 'image' | 'video' | 'web' | 'template' | 'html';
  mime: string;
  size: number;
  checksum: string;
  width: number | null;
  height: number | null;
  duration_sec: number | null;
  default_dwell_sec: number;
  tags: string[];
  /** 소리 크기(0~100) — 영상만 뜻이 있다. 옛 서버는 주지 않는다(100 과 같다). */
  volume?: number;
  status: 'processing' | 'ready' | 'failed';
  /** 처리 실패의 까닭. 옛 서버는 주지 않을 수 있다. */
  status_reason?: string;
  /**
   * 영상 변환 — 화면이 못 트는 영상을 모든 화면이 푸는 재생본으로(서버 `services/transcode.ts`). 변환한 적이 없으면 null.
   * 옛 서버는 주지 않는다.
   */
  transcode?: {
    state: 'queued' | 'running' | 'done' | 'failed';
    progress: number;
    reasons: string[];
    error: string;
  } | null;
  /** 화면에 나가는 것 — 변환본(`proxy`)이 있으면 원본 대신 그것이 나간다. */
  playback?: 'proxy' | 'original';
  source_url: string;
  archived: boolean;
  /**
   * 이 콘텐츠가 들어 있는 편성.
   *
   * "왜 이건 삭제가 안 되지" 에 목록에서 바로 답한다. 삭제를 눌러 보고 나서야
   * 알게 되면, 여러 개를 고른 경우 무엇 때문에 막혔는지 하나씩 되짚어야 한다.
   */
  used_in?: AssetUsage[];
  /**
   * 배포 현황 — 어느 경로로 어느 화면에 나가는가. `getAsset(id, { reach: true })` 로 물었을 때만 온다.
   * 옛 서버는 주지 않는다(그때는 비어 있다).
   */
  reach?: ContentReach;
  created_at: string;
  updated_at: string;
  /** 콘솔 미리보기용 서명 URL. 템플릿·파일 없는 자산은 null 이다. */
  preview_url?: string | null;
  /** 템플릿 자산의 내용. `type === 'template'` 일 때만 있다. */
  template?: TemplatePayload | null;
  /** 게시 기간(ISO). 시작 전·종료부터는 어느 편성에 있어도 나가지 않는다. 옛 서버는 주지 않는다. */
  valid_from?: string | null;
  valid_until?: string | null;
  /**
   * 올린 HTML 의 화면 맞춤(2026-10-20) — 기준 크기는 `width`·`height`(null 이면 화면 크기에 맞춰 늘고 주는 HTML).
   * `html_measured_at` 이 null 이면 아직 재지 않았다. 다른 종류에는 없다.
   */
  html_fit?: HtmlFit;
  /**
   * 올린 HTML 이 바깥에서 불러오는 출처(2026-11-03) — `https://cdn.example.com` 꼴. 아직 훑지 않은 옛 HTML 은 목록에서 null
   * (상세를 열면 채워진다). 옛 서버는 주지 않는다.
   */
  html_external?: string[] | null;
  html_measured_at?: string | null;
  html_size_manual?: boolean;
  /** 적용할 화면(2026-10-20) — 콘솔이 그 화면 크기로 미리 보이는 기준. 정하지 않았으면 null. 올린 HTML 만. */
  html_target_display_id?: string | null;
  /** 템플릿으로 등록했으면 [템플릿 만들기] 에 보일 이름·설명(2026-10-30). 아니면 null(옛 서버는 주지 않는다). */
  template_entry?: AssetTemplateEntry | null;
}

export interface AssetTemplateEntry {
  name: string;
  description: string;
  registered_at: string;
  registered_by: string;
}

export interface PlaylistItem {
  asset_id: string;
  dwell_sec: number;
  transition: Transition;
  enabled: boolean;
}

export interface Playlist {
  id: string;
  name: string;
  description: string;
  default_transition: Transition;
  items: PlaylistItem[];
  item_count: number;
  enabled_count: number;
  /**
   * 지금 실제로 나가는 항목 수 — 켜져 있고 자산이 살아 있는(보관되지 않은) 것.
   *
   * `enabled_count` 는 줄의 스위치만 센다. 담긴 자산을 보관하면 둘이 갈라지고,
   * 그때 목록이 `enabled_count` 를 보이면 "2 / 2" 인데 화면에는 하나만 나간다.
   */
  playable_count: number;
  revision: number;
  archived: boolean;
  updated_at: string;
}

export interface Assignment {
  kind: 'none' | 'playlist' | 'schedule';
  playlist_id: string | null;
  schedule_id: string | null;
}

export interface Display {
  id: string;
  device_id: string;
  name: string;
  location: string;
  region: string;
  timezone: string | null;
  tags: string[];
  orientation: 'landscape' | 'portrait';
  /** 그릴 때 적용하는 회전각. orientation 은 이 값의 요약이다. */
  rotation: 0 | 90 | 180 | 270;
  group_id: string | null;
  assignment: Assignment;
  channel: 'stable' | 'canary';
  pinned_version: string | null;
  /** 운영 시간 — 환경 설정 따르기·늘 켜기·이 화면만. 옛 서버는 주지 않는다(따르기와 같다). */
  hours?: { mode: 'inherit' | 'always' | 'custom'; rules: HoursRule[] };
  /** 소리 — 음량(0~100)·음소거. 옛 서버는 주지 않는다(100·켜짐과 같다). */
  audio?: { volume: number; muted: boolean };
  /** 기기 상태 — 화면 상세에서만 온다(목록에는 없다). 보고한 적이 없으면 null, 옛 서버는 주지 않는다. */
  health?: DeviceHealthView | null;
  /**
   * 콘솔에서 정한 정비 모드 PIN — 언제·누가만. **PIN 자체는 서버에도 없다**(해시만).
   * null 이면 화면이 기기 설정의 PIN(없으면 0000)을 쓴다. 옛 서버 응답에는 없을 수 있다.
   */
  maintenance_pin?: { set_at: string; set_by: string } | null;
  /** 원격 설정·명령 — 콘솔에서 정한 값과 화면이 알려 온 지금 값. 옛 서버 응답에는 없다. */
  agent_control?: AgentControl;
  /**
   * 목록에서 감춘 화면.
   *
   * **삭제와 다르다.** 화면은 그대로 재생하고 증빙도 계속 쌓인다. 철거 예정이거나
   * 장기 수리 중인 것을 목록에서 치우되 이력은 남기기 위한 것이다.
   */
  hidden: boolean;
  online: boolean;
  /**
   * 에이전트 버전 — 받아야 할 것(`wanted`)과 뒤처졌는지(`behind`).
   *
   * 승격은 화면이 물어봐야 일어난다. 묻지 않는 화면은 아무 소리도 내지 않으므로
   * 서버가 판정해서 준다. 옛 서버 응답에는 없다.
   */
  agent?: {
    wanted: string | null;
    behind: boolean;
    /** 업데이트를 물을 때 알려 온 플랫폼(`windows-x86_64` …). 목표가 롤아웃과 다르면 그 플랫폼에 롤아웃 버전 산출물이 없는 것이다. */
    platform?: string | null;
    /** 화면이 알려 온 업데이트 단계 — 뒤처짐의 **이유**. 옛 플레이어는 보내지 않아 null. */
    update?: { phase: string; version: string | null; detail: string | null; at: string } | null;
  };
  status: {
    last_heartbeat_at: string | null;
    current_asset_id: string | null;
    current_playlist_id: string | null;
    resolved_revision: string | null;
    app_version: string | null;
    screen_w: number | null;
    screen_h: number | null;
    cache_bytes: number | null;
    /**
     * 기기가 더 둘 수 있는 여유. **null 은 "모른다"** 다 — 0 과 다르다.
     *
     * `source` 가 뜻을 가른다: `disk` 는 파티션의 실제 여유(네이티브 셸), `quota` 는
     * 브라우저가 이 오리진에 허락한 할당량이다. 옛 에이전트·셸 없는 브라우저 키오스크는
     * 아예 보고하지 않는다. 옛 서버 응답에는 이 필드가 없을 수 있다.
     */
    storage?: DisplayStorage | null;
    /**
     * 기기가 보고한 MAC(대문자 콜론 꼴). **null 은 "모른다"** — 옛 에이전트·브라우저 키오스크, 그리고 안드로이드 11+
     * 일반 앱(기기 소유자가 아니면 OS 가 주지 않는다). 옛 서버 응답에는 없다.
     */
    mac?: string | null;
    last_error: string | null;
    /**
     * 현장에서 정비 모드를 열어 둔 채인가. 그동안 재생·증빙이 멈춘다. 0.7.17 에이전트부터는 콘솔의 [정비 모드 해제] 로
     * 풀고, 30분 동안 아무도 만지지 않으면 저절로 풀린다. 옛 서버 응답에는 없을 수 있다.
     */
    maintenance?: boolean;
    /**
     * 패널(TV) 전원 — 운영 시간 밖에 패널을 끄는 화면이 보고한다(0.7.17+). `off` 면 쉬는 동안 꺼 두었다.
     * 끄기를 쓰지 않는 화면·옛 서버는 null 이거나 없다.
     */
    panel?: 'on' | 'off' | null;
  };
  /** 직접 전송 상태. null 이면 편성대로 나간다. */
  direct: DirectPush | null;
  paired_at: string;
  updated_at: string;
}

/**
 * 정비 화면의 설정 중 콘솔에서도 정하는 것 — 자동 업데이트·부팅 시 자동 실행, 그리고 재부팅·
 * 앱 종료·전원 종료 명령.
 *
 * 값마다 null 이면 **기기에 맡긴다**(정비 화면에서 고른 대로). 서버 주소·네트워크는 원격으로
 * 두지 않는다 — 잘못 바꾸면 되돌릴 길이 없다.
 */
export interface AgentControl {
  /** 콘솔에서 정한 값. null 이면 둘 다 기기에 맡겼다. */
  settings: { auto_update: boolean | null; autostart: boolean | null; set_at: string; set_by: string } | null;
  /**
   * 화면이 하트비트로 알려 온 지금 값과 할 수 있는 일. 값마다 null 은 읽을 수 없음(셸 없음).
   * **문서째 null 이면 원격 설정을 모르는 옛 에이전트(0.7.6 미만)** 다.
   */
  reported: {
    auto_update: boolean | null;
    autostart: boolean | null;
    reboot: boolean | null;
    power_off: boolean | null;
    /** 알아듣는 원격 명령. 옛 서버는 주지 않는다 — 그때는 재부팅만 아는 것으로 본다. */
    commands?: AgentCommandKind[];
    at: string;
  } | null;
  /** 아직 화면이 받지 않은 명령. 10분이 지나면 없는 것으로 친다(`expires_at`). */
  pending: { id: string; kind: AgentCommandKind; requested_at: string; requested_by: string; expires_at: string } | null;
  /** 화면이 마지막으로 보고한 명령 결과 — `started` 는 걸기 직전에 보낸다. */
  last_command: { id: string; kind: AgentCommandKind; state: 'started' | 'failed'; detail: string | null; at: string } | null;
}

/** 콘솔이 화면에 거는 명령. 앱 종료·전원 종료는 원격으로 되돌릴 수 없다. */
export type AgentCommandKind = 'reboot' | 'quit' | 'power_off' | 'exit_maintenance';

/** 여러 화면에 같은 명령을 건 결과(`POST /displays/bulk-command`) — 걸 수 없던 화면은 까닭과 함께. */
export interface BulkCommandResult {
  ok: boolean;
  kind: AgentCommandKind;
  /** 새로 건 화면 수. */
  issued: number;
  /** 같은 명령이 이미 걸려 있던 화면 수 — 두 번 걸지 않는다. */
  already: number;
  /** 걸지 않은 화면 — `code` 는 한 대짜리 409 와 같다(`display_offline`·`agent_outdated`·…). */
  skipped: Array<{ id: string; name: string; code: string; message: string }>;
}

export interface DirectPush {
  playlist_id: string;
  expires_at: string | null;
  expired: boolean;
  note: string;
  created_by: string;
  created_at: string;
  /**
   * 화면이 받아 간 시각. null 이면 **전달 대기** — 꺼진 화면에 걸어 둔 것이 아직 나가지 않았다.
   * 옛 서버는 주지 않는다(그때는 켜진 화면에만 보냈으니 받은 것으로 본다).
   */
  delivered_at?: string | null;
}

/**
 * 화면이 로컬에 들고 있는 콘텐츠 파일 하나.
 *
 * **화면이 보고한 것이다.** 서버는 무엇을 내려보냈는지는 알아도 그것이 지금도 그
 * 기기에 남아 있는지는 모른다 — 웹뷰가 저장 공간이 빠듯해 임의로 버렸을 수도,
 * 사람이 앱 데이터를 지웠을 수도 있다.
 *
 * 앞으로의 예정(`next_play_at`)과 지난 이력(`last_played_at`)을 함께 주는 이유는
 * 판단의 방향이 반대이기 때문이다. 예정만 보면 "요즘 안 나가는 파일"을 가려낼 수 없고,
 * 이력만 보면 "지금 지우면 곧 다시 받는다"를 경고할 수 없다.
 */
/**
 * 이 항목이 **파일로서** 어떤 상태인가.
 *
 * 편성에 걸린 것과 화면이 들고 있는 것을 한 목록에 싣기 때문에 줄마다 이 구분이
 * 필요하다. 넷을 가르지 않으면 "빈 칸"의 뜻이 서로 다른 세 가지로 뭉개진다.
 */
export type CachedFileState =
  /** 화면이 들고 있다고 보고했다. */
  | 'cached'
  /** 템플릿·웹 주소 — 내려받을 바이트가 애초에 없다. 비어 있는 것이 정상이다. */
  | 'no_file'
  /**
   * 앞으로의 시간표에는 있지만 지금 편성은 아니라서 아직 안 받았다. **정상이다.**
   * 화면은 지금 편성의 자산만 미리 받고, 미래 구간은 그때 가서 받는다.
   */
  | 'not_yet'
  /** **지금 나가야 하는데** 보고된 목록에 없다. "왜 저게 안 나오지"의 답이 대개 여기 있다. */
  | 'missing_file'
  /**
   * 지금 나가야 하는데 마지막 보고에 없다 — 그 보고가 낡아 **아직 판정하지 않았다**. 목록 조회가 새 보고를
   * 요청하므로(화면 깨우기) 곧 `cached`·`missing_file` 로 바뀐다. 낡은 보고로 "못 받음" 을 단정하지 않으려는 것.
   */
  | 'checking'
  /** 아직 보고를 받지 못했다(옛 에이전트 포함). 들고 있는지 모른다. */
  | 'unknown';

export interface CachedFile {
  /** 자산 id. 사내 글꼴(`brand: 'font'`)은 자산이 아니라 `brand-font:<판>` 이다. */
  asset_id: string;
  /** 자산이 지워졌으면 그 사실을 적은 문구가 온다. */
  title: string;
  type: Asset['type'] | null;
  /** 파일로서의 상태. 빈 크기·빈 시각의 뜻을 이 값이 정한다. */
  state: CachedFileState;
  /** 화면이 보고한 크기. 보고에 없거나 크기를 적어 두기 전에 받은 항목은 0 이다. */
  bytes: number;
  cached_at: string | null;
  /** 자산 기록이 이미 지워졌는가. 그렇다면 이 파일은 다시 쓰일 일이 없다. */
  missing: boolean;
  archived: boolean;
  /** 지금 이 화면의 편성에 들어 있는가. 지우면 화면이 곧바로 다시 받는다. */
  in_playback: boolean;
  /** 앞으로 처음 나갈 시각. 내다본 구간 안에 없으면 null. */
  next_play_at: string | null;
  segment_count: number;
  last_played_at: string | null;
  play_count: number;
  /** 지금 삭제 지시가 걸려 있는가. 화면이 아직 수행하지 않았다는 뜻이다. */
  evicting: boolean;
  /**
   * 템플릿이 쓰는 파일 — `logo` 회사 로고, `font` 사내 글꼴(환경 설정 ▸ 브랜드). 편성에 없어도 늘 쓰므로 지울 수 없다.
   * 옛 서버는 주지 않는다.
   */
  brand?: 'logo' | 'font' | null;
  /**
   * 이 파일을 품은 콘텐츠(2026-11-04) — 반반 화면·구역 나누기 템플릿에 넣은 그림·영상·HTML, 올린 HTML 이 품은 영상이면 그
   * 템플릿·HTML 의 자산 id. 표가 그 줄 아래에 묶어 보이고 템플릿 줄로 함께 지운다. 옛 서버는 주지 않는다.
   */
  used_by?: string[];
}

/**
 * 화면이 보고한 저장공간.
 *
 * `disk` 는 파티션의 실제 여유(네이티브 셸이 읽는다), `quota` 는 브라우저가 이 오리진에
 * 허락한 할당량이다 — 디스크 여유와 다르고 상황에 따라 변한다.
 */
export interface DisplayStorage {
  free_bytes: number;
  total_bytes: number;
  source: 'disk' | 'quota';
}

/** 저장 공간 확인에 넘기는 것 — 화면과, 플레이리스트·스케줄·바로 보낼 콘텐츠 중 하나. */
export type StorageCheckInput = { display_ids: string[] } & (
  | { playlist_id: string }
  | { schedule_id: string }
  | { asset_ids: string[] }
);

export interface StorageCheckRow {
  display_id: string;
  name: string;
  /** 화면이 들고 있다고 보고하지 않은 이미지·영상의 합 — 더 받아야 하는 양. */
  required_bytes: number;
  /** 화면이 보고한 여유. 모르면 null. */
  free_bytes: number | null;
  /** 들어가는가. null 은 모른다 — 아무것도 그리지 않는다. */
  fits: boolean | null;
}

export interface StorageCheckResult {
  /** 편성 전체가 화면에 보내는 바이트(재생본이 있으면 재생본). */
  total_bytes: number;
  items: StorageCheckRow[];
}

/**
 * 목록의 상태 따라가기 한 줄 — 목록 한 줄(`Display`)에서 **사람이 손대지 않아도 바뀌는 칸**만.
 * 같은 이름·같은 모양이라 받아 둔 줄에 그대로 덮어쓴다(`mergeDisplayStatus`).
 */
export interface DisplayLiveStatus {
  id: string;
  online: boolean;
  agent: NonNullable<Display['agent']>;
  status: Pick<Display['status'], 'last_heartbeat_at' | 'app_version' | 'storage' | 'last_error' | 'maintenance'>;
  direct: DirectPush | null;
  /** 사람이 이 화면을 마지막으로 고친 시각 — 받아 둔 줄과 다르면 목록째 다시 받는다. 옛 서버는 주지 않는다. */
  updated_at?: string;
}

export interface DisplayStatusResponse {
  items: DisplayLiveStatus[];
  /** 상한(5,000대)에서 잘렸는가 — 목록도 같은 앞부분에서 잘린다. */
  truncated: boolean;
  as_of: string;
}

export interface DisplayCache {
  /**
   * 이 화면의 에이전트가 목록 보고를 할 줄 아는가.
   *
   * **거짓이면 기다려도 영영 차지 않는다.** 옛 에이전트는 목록을 올리라는 지시 자체를
   * 모르고 조용히 무시하므로, 이것을 말해 주지 않으면 운영자는 멀쩡한 화면을 고장으로
   * 읽는다. 재생에는 영향이 없다 — 보고만 못 하는 것이다.
   */
  agent_supported: boolean;
  /** 화면이 보고한 에이전트 버전(순수 semver). 보고한 적이 없으면 null. */
  agent_version: string | null;
  /** 목록 보고가 들어간 최초 에이전트 버전. */
  min_agent_version: string;
  reported_at: string | null;
  /** 보고가 낡았는가. 낡아도 내용은 그대로 온다 — 오프라인 화면은 그것뿐이다. */
  stale: boolean;
  never_reported: boolean;
  total_bytes: number;
  truncated: boolean;
  /** 자산으로 환원되지 않은 주소의 수. 지워진 자산의 파일이 남아 있는 경우다. */
  unknown_count: number;
  /** 편성에 있는데 화면이 들고 있지 않은 항목의 수. 0 이 아니면 들여다볼 이유가 있다. 보고가 낡았으면 세지 않는다. */
  missing_count: number;
  /** 낡은 보고에 없어 새 보고를 기다리는 항목의 수(`checking`). 이 값이 없는 옛 서버는 0 으로 본다. */
  checking_count?: number;
  history_days: number;
  lookahead_min: number;
  items: CachedFile[];
}

/** 화면 월 한 칸 — 화면 정보 + 지금 나가는 것. */
export interface WallCard extends Display {
  playback: {
    source: 'emergency' | 'direct' | 'closed' | 'campaign' | 'display' | 'group' | 'none';
    revision: string;
    item_count: number;
    empty: boolean;
  };
  now_playing: {
    asset_id: string;
    title: string;
    type: Asset['type'];
    dwell_sec: number;
    origin: 'base' | 'campaign' | 'emergency' | 'direct';
    template: TemplatePayload | null;
    preview_url: string | null;
    /** 올린 HTML 의 기준 크기와 화면 맞춤(2026-10-20). 옛 서버는 보내지 않는다. */
    width?: number | null;
    height?: number | null;
    fit?: HtmlItemFit;
    /** 화면이 직접 보고한 것인가. 아니면 편성상 나갈 것을 보여주는 중이다. */
    reported: boolean;
    /** 보고한 것이 지금 편성에 없다 — 아직 새 편성을 못 받았다는 뜻이다. */
    stale: boolean;
  } | null;
  /** 화면이 직접 보고한 송출 상태. 낡았거나 보고한 적이 없으면 null. */
  screen: ScreenReport | null;
  /**
   * 화면의 마지막 보고 — 낡았어도 하루 안이면 온다. 새 캡처를 기다리는 동안 "이전 모습"으로 건다.
   * 옛 서버는 보내지 않는다.
   */
  last_screen?: ScreenReport | null;
}

/**
 * 화면이 직접 보고한 송출 상태.
 *
 * `now_playing.preview_url`(자산 썸네일)과 **다른 것**이다. 저쪽은 "이것이 나가야
 * 한다"이고 이쪽은 "화면에 이것이 떠 있다"다. 화면이 검거나 늘어났거나 편성을 놓친
 * 것은 자산 썸네일로는 영영 보이지 않는다.
 *
 * **보고 방식이 둘이다.**
 *   - `frame` — 화면을 찍은 그림. 사진·웹(URL)·템플릿.
 *   - `video` — 그림이 아니라 **재생 위치**. 콘솔이 같은 영상에서 그 위치의 장면을 꺼내 건다.
 */
export interface ScreenReport {
  kind: 'frame' | 'video';
  /** `kind === 'frame'` 일 때의 그림 주소. */
  url: string | null;
  width: number;
  height: number;
  /** `kind === 'video'` 일 때의 재생 위치(초). */
  position_sec: number | null;
  duration_sec: number | null;
  paused: boolean;
  taken_at: string;
  /** 보고 당시 화면에 떠 있던 자산. 편성 보고와 어긋나면 화면이 편성을 놓친 것이다. */
  asset_id: string | null;
}

export interface WallResponse {
  items: WallCard[];
  next_cursor: null;
  as_of: string;
  /**
   * 화면의 보고 주기(초). 콘솔이 "얼마나 조용한가" 를 스스로 셈해, 온라인 판정이
   * 뒤집히기 전에 침묵을 먼저 드러내는 데 쓴다(`linkState`). 옛 서버는 주지 않는다.
   */
  poll_sec?: number;
}

/**
 * 모니터링 판의 위치 카드 한 장 — 그 위치의 화면들을 담는 상자.
 *
 * `key` 는 **위치 이름 그대로**다(빈 문자열은 위치 미지정). 화면의 위치는 화면이 들고 있는
 * 값이라 우리가 발급한 id 가 없다 — 이름을 바꾸면 그 카드는 새 카드가 된다.
 */
/** 사업장 그림의 영역 하나 — 열쇠는 `siteLayout.ts` 가 짓는다(`r:`·`f:`·`b:`). */
export interface SiteLayoutItem {
  key: string;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface WallPlace {
  key: string;
  x: number;
  y: number;
  w: number;
  h: number;
  /** 이 위치 카드 **안쪽 판**(다시 24칸)에서의 화면 자리와 크기. */
  cards: Array<{ id: string; x: number; y: number; w: number; h: number }>;
}

export interface FleetSummary {
  total: number;
  online: number;
  offline: number;
  unassigned: number;
  erroring: number;
  /**
   * 지금 손대야 할 화면 수 — 오프라인이거나 오류를 보고한 화면.
   *
   * **서버가 센 집합이다.** 콘솔에서 `offline + erroring` 으로 더하면 오류를 보고한 뒤
   * 꺼진 화면이 두 번 세어져, "주의 필요 2" 를 눌러 연 목록에 한 대만 서 있게 된다.
   * 목록도 같은 뜻으로 거른다(`GET /api/v1/displays?status=attention`).
   */
  attention: number;
  /**
   * 켜져 있고 편성도 있지만 지금 운영 시간 밖이라 쉬는 화면 수. 증빙을 남기지 않는 것이 정상이다.
   * 옛 서버는 주지 않는다(0 으로 본다).
   */
  closed?: number;
  /**
   * 정비 모드로 빠져 있는 화면 수.
   *
   * 온라인이지만 **재생도 증빙도 멈춰 있다.** 콘솔에서 풀 수 없어 현장에 연락해야
   * 하므로 오프라인·오류와 같은 무게로 다룬다. 서버는 처음부터 세어 보냈는데 이 타입에
   * 없어서 콘솔 어디에서도 읽지 못했다 — 정비 모드는 화면 한 대씩 열어야만 보였다.
   */
  maintenance: number;
  /** 숨긴 화면 수. 위 집계에는 들어 있지 않다 — 치워 둔 것이 몇 대인지는 보여야 한다. */
  hidden: number;
  offline_ratio: number;
  as_of: string;
  /**
   * 위치별 온·오프라인 — `locations=1` 로 물었을 때만 온다. 위치를 적지 않은 화면은 `name` 이 빈 문자열이다.
   *
   * 대시보드가 이것을 그리려고 화면 전부(최대 5,000대)를 30초마다 받던 것을 서버가 센다.
   */
  locations?: Array<{ name: string; total: number; online: number; offline: number }>;
}

export interface Group {
  id: string;
  name: string;
  assignment: Assignment;
  display_count: number;
}

/**
 * 쓰이고 있는 위치 하나 — 콘솔의 위치 드롭다운이 고르는 값.
 *
 * 위치는 별도 엔티티가 아니라 화면에 적힌 문자열이다. 그래서 "등록된 위치"란
 * **지금 어느 화면엔가 적혀 있는 이름**을 말한다. 마지막 화면이 그 이름을 떠나면
 * 목록에서도 사라진다.
 *
 * `display_count` 는 숨긴 화면도 센다 — 함대 집계와 뜻이 다르다. 고르기 위한
 * 목록이라 숨긴 화면이 있는 지점도 이름이 남아야 하기 때문이다.
 */
export interface DisplayLocation {
  name: string;
  display_count: number;
}

/** 위치 이름 바꾸기·지우기의 결과. */
export interface LocationChangeResult {
  ok: boolean;
  /** 바꾼 화면 수(숨긴 화면 포함). */
  displays: number;
  /** 바꿀 이름을 이미 쓰던 화면이 있어 두 지점이 합쳐졌는가. */
  merged: boolean;
  /** 함께 옮기거나 버린 사업장 구성 줄 수. */
  site_entries: number;
  /** 배치(모니터링 판·사업장 그림)를 함께 옮긴 계정 수. */
  layouts: number;
}

export interface ScheduleRule {
  id: string;
  label: string;
  days: number[];
  start: string;
  end: string;
  /** 이 규칙이 트는 것 — 플레이리스트 또는 콘텐츠 하나. 서버가 둘 중 하나만 받는다. */
  playlist_id: string | null;
  asset_id: string | null;
  priority: number;
}

export interface ScheduleSegment {
  from: string;
  to: string;
  playlist_id: string | null;
  asset_id: string | null;
  rule_id: string | null;
}

/** 하루 미리보기. `from`~`to` 는 스케줄 타임존의 자정~자정이라 24시간이 아닐 수 있다. */
export interface SchedulePreview {
  schedule_id: string;
  timezone: string;
  date: string;
  from: string;
  to: string;
  step_minutes: number;
  segments: ScheduleSegment[];
}

export interface Schedule {
  id: string;
  name: string;
  timezone: string;
  default_playlist_id: string | null;
  /** 평소 편성이 콘텐츠 하나일 때(2026-10-21) — `default_playlist_id` 와 함께 차지 않는다. 옛 서버는 주지 않는다. */
  default_asset_id?: string | null;
  rules: ScheduleRule[];
  revision: number;
  updated_at: string;
  /** 이 스케줄이 실제로 나가는 화면 수 — 직접 배정 + 그룹에서 물려받는 것. */
  display_count: number;
  /** 이 스케줄을 가리키는 그룹 수. 화면이 0대여도 참조는 참조라 삭제를 막는다. */
  group_count: number;
}

export interface Daypart {
  days: number[];
  start: string;
  end: string;
}

export interface Campaign {
  id: string;
  name: string;
  playlist_id: string;
  target: {
    kind: 'all' | 'tags' | 'groups' | 'displays';
    tags: string[];
    group_ids: string[];
    display_ids: string[];
  };
  start_at: string | null;
  end_at: string | null;
  dayparts: Daypart[];
  timezone: string | null;
  priority: number;
  mode: 'replace' | 'blend';
  blend_every: number;
  enabled: boolean;
  status: 'active' | 'scheduled' | 'ended' | 'disabled';
  /** 같은 우선순위끼리는 **먼저 만든 쪽이 이긴다** — 가려진 캠페인을 가릴 때 쓴다. */
  created_at: string;
}

export interface Emergency {
  id: string;
  name: string;
  /** 덮어 트는 플레이리스트. 화면 비우기면 null. */
  playlist_id: string | null;
  /** 화면 비우기 — 틀 것 없이 대상 화면을 검게 둔다. 옛 서버는 주지 않는다. */
  blank?: boolean;
  target?: Campaign['target'];
  start_at?: string | null;
  end_at?: string | null;
  enabled: boolean;
  active: boolean;
  /**
   * 내 담당 범위의 화면에 닿는가 — 범위가 없는 계정은 늘 참(2026-10-22). 목록은 범위와 상관없이 모두 오고, 종 알림은
   * 닿는 것만 센다. 옛 서버는 주지 않는다.
   */
  in_scope?: boolean;
  created_at: string;
}

export interface ResolvedItem {
  assetId: string;
  /** 콘솔 표시용. 서버가 자산에서 채워 준다. */
  title?: string;
  /** 콘솔이 그림을 띄울 수 있는 서명 URL. 항목의 `url` 은 디바이스 전용이다. */
  preview_url?: string | null;
  type: 'image' | 'video' | 'web' | 'template' | 'html';
  url: string;
  mime: string;
  /** 영상 길이(초). 이미지·웹·템플릿은 null — 영상은 끝까지 재생하므로 화면에 머무는 시간이 곧 이것이다. */
  durationSec?: number | null;
  dwellSec: number;
  transition: string;
  origin: 'base' | 'campaign' | 'emergency';
  template?: TemplatePayload | null;
  /** 콘텐츠 크기 — 올린 HTML 은 기준 크기(늘고 주는 HTML 은 null). */
  width?: number | null;
  height?: number | null;
  /** 올린 HTML 을 기준 크기로 그려 화면에 맞추는 법 — 기준 크기가 있을 때만(2026-10-20). */
  fit?: HtmlItemFit;
}

export interface TraceEntry {
  stage: string;
  matched: boolean;
  reason: string;
  ref?: string;
}

export interface DisplayPreview {
  at: string;
  source: string;
  playlist_id: string | null;
  timezone: string;
  revision: string;
  empty: boolean;
  items: ResolvedItem[];
  trace: TraceEntry[];
  /** 화면이 마지막으로 보고한 자산. 없으면 편성 기준으로 보여준다. */
  reported_asset_id: string | null;
  /** 화면이 직접 보고한 송출 상태. 다른 시각을 보고 있을 때는 null 이다. */
  screen: ScreenReport | null;
  /** 화면의 마지막 보고 — 벽 카드의 `last_screen` 과 같다. 다른 시각을 보고 있을 때는 null 이다. */
  last_screen?: ScreenReport | null;
}

export interface AuditEntry {
  id: string;
  actor: string;
  actor_issuer: string | null;
  action: string;
  resource_type: string;
  resource_id: string;
  before: unknown;
  after: unknown;
  created_at: string;
}

export type Role = 'viewer' | 'auditor' | 'content-manager' | 'admin' | 'system';

export interface Me {
  kind: 'user' | 'service';
  id: string;
  role: Role;
  issuer: string;
  tenant_id: string;
  capabilities: string[];
  /** 어떤 방식으로 붙었나. 로그아웃 버튼을 보일지 정한다. */
  auth: 'local' | 'host' | 'service';
  account: {
    id: string;
    email: string;
    name: string;
    must_change_password: boolean;
    /** `oidc` 면 사내 계정 — 비밀번호는 사내 계정 시스템에서 바꾼다. 옛 서버는 없다(로컬). */
    auth_source?: 'local' | 'oidc';
  } | null;
  /**
   * 담당 범위 — 맡은 화면 그룹. null 이면 고객사 전체. 범위가 있으면 맡은 그룹의 화면만 보이고, 캠페인·긴급의
   * 대상은 맡은 그룹·화면만 고를 수 있다(서버 `services/scope.ts`).
   */
  scope: { group_ids: string[]; groups: Array<{ id: string; name: string }> } | null;
  /**
   * 이 서버에서 이 사람이 쓰는 선택 기능 — 메뉴를 낼지(`nav.ts` 의 `navCapabilities`). 서버가 그 경로에서 다시 본다.
   * 옛 서버는 주지 않는다(없는 것으로).
   */
  features?: { hub?: boolean; hub_monitor?: boolean };
}

/* ───────────────────────── 통합 관제 (`/api/v1/hub`) ───────────────────────── */

/**
 * 모아 보는 서버의 마지막 상태. `unreachable` 은 **화면 장애가 아니라** 그 서버에 닿지 않는 것이다(그리드·망).
 * `unauthorized` 는 그 서버가 토큰을 거절했거나 토큰이 없다(백업에서 되살린 뒤).
 */
export type HubState = 'ok' | 'unreachable' | 'unauthorized' | 'error' | 'pending';

export interface HubSummary {
  total: number;
  online: number;
  offline: number;
  attention: number;
  maintenance: number;
  unassigned: number;
  erroring: number;
  closed: number;
  /** 위치별 — 오프라인이 많은 곳부터. 옛 서버는 빈 목록. */
  locations: Array<{ name: string; total: number; online: number; offline: number }>;
  as_of: string | null;
}

export interface HubServerView {
  id: string;
  name: string;
  url: string;
  token_set: boolean;
  /** 넣은 토큰의 끝 네 자리. 토큰 자체는 서버가 내보내지 않는다. */
  token_hint: string;
  /** 그 토큰이 그 서버에서 무엇인가. `can_command` 가 거짓이면 명령을 보낼 수 없다(조회 역할 토큰). */
  remote: { tenant_id: string; role: string; can_command: boolean } | null;
  state: HubState;
  last_error: string;
  last_ok_at: string | null;
  last_checked_at: string | null;
  /** 마지막으로 받은 지 세 주기(90초)가 넘었다 — 요약은 끊기기 전의 것이다. */
  stale: boolean;
  summary: HubSummary | null;
  created_at: string;
  updated_at: string;
}

/**
 * 사업장 구성 한 줄 — 출처(이 서버 `local` · 통합 관제 서버 id)의 위치(건물)를 지역·공장에 붙인다.
 * `location` 이 `null` 이면 그 출처의 기본값. 빈 칸은 위의 값을 따른다.
 */
/**
 * 주소 한 덩어리 — 우편번호·기본 주소(주소 DB 에서 고른 도로명주소)·상세 주소, 그리고 지도에 놓을 좌표(주출입구, WGS84).
 * 좌표는 주소 DB 의 「위치정보요약DB」 가 들어 있을 때만 검색 결과에 붙는다 — 없으면 null.
 */
export interface SiteAddress {
  postal_code: string;
  address: string;
  address_detail: string;
  latitude: number | null;
  longitude: number | null;
}

export interface SiteEntry {
  source: string;
  location: string | null;
  region: string;
  site: string;
  /** 이 건물(위치 null 이면 출처 기본값)의 주소 — 모니터링 지도에 놓는 자리. 없으면 null. */
  address?: SiteAddress | null;
}

/** 주소 DB 에 들어 있는 자료 한 가지(주소 또는 위치)의 요약. */
export interface AddressDbSummary {
  loaded: boolean;
  count: number;
  base_date: string | null;
  source: string | null;
  file_name: string | null;
  loaded_at: string | null;
  loaded_by: string | null;
}

/** 주소 DB 넣기 한 건 — 진행 중이거나 마지막 것. */
export interface AddressDbJob {
  state: 'running' | 'done' | 'failed';
  phase?: string;
  dataset: 'address' | 'location';
  mode: 'full' | 'change';
  file_name: string;
  started_at: string;
  started_by?: string | null;
  read_bytes?: number;
  total_bytes?: number;
  rows?: number;
  percent?: number | null;
  finished_at?: string;
  count?: number;
  error?: string;
}

export interface AddressDbStatus extends AddressDbSummary {
  /** 「위치정보요약DB」 — 들어 있어야 검색 결과에 좌표가 붙는다. */
  location: AddressDbSummary;
  job: AddressDbJob | null;
}

export interface AddressSearchResult {
  id: string;
  postal_code: string;
  road_address: string;
  jibun_address: string;
  building_name: string;
  /** 저장할 기본 주소 — 도로명주소에 참고 항목(법정동·공동주택 이름). */
  address: string;
  latitude?: number;
  longitude?: number;
}

export interface AddressSearchResponse {
  /** 주소 DB 가 들어 있다 — false 면 검색할 수 없다(직접 입력). */
  loaded: boolean;
  base_date?: string | null;
  /** 좌표 자료가 들어 있다. */
  located?: boolean;
  results: AddressSearchResult[];
  more?: boolean;
  /** 뒤에 적은 상세 주소를 빼고 찾았다. */
  relaxed?: boolean;
  hint?: string;
}

export interface SiteMapView {
  /** 조직 이름 — 세 층(지역 › 사업장 › 위치) 위의 한 이름. 빈 문자열이면 정하지 않았다("전체"). */
  name: string;
  /** 층마다 화면에 적을 말 — 빈 문자열은 기본(`siteLabels.ts`). 옛 서버는 이 칸이 없다. */
  labels?: { region: string; site: string; location: string };
  entries: SiteEntry[];
  revision: number;
  updated_at: string | null;
  updated_by: string;
  /** 이 서버를 가리키는 출처 이름. */
  local_source: string;
}

export interface HubServerList {
  module: { enabled: boolean; beta: boolean; exposed: boolean };
  poll_sec: number;
  items: HubServerView[];
  as_of: string;
}

/** 그 서버의 화면 한 줄 — 통합 관제가 그리는 칸만(서버가 그 서버의 판과 상관없이 같은 모양으로 고른다). */
export interface HubDisplay {
  id: string;
  name: string;
  location: string;
  online: boolean;
  last_heartbeat_at: string | null;
  app_version: string;
  last_error: string | null;
  maintenance: boolean;
  /** 그 서버가 받아 둔 기기 MAC. 옛 판 서버·모르는 기기는 null(옛 판 서버는 이 칸이 없다). */
  mac?: string | null;
  /** 알아듣는 명령. null 이면 원격 명령을 모르는 옛 에이전트. */
  commands: AgentCommandKind[] | null;
  can_reboot: boolean;
  can_power_off: boolean;
  /** 보내 두었는데 화면이 아직 받지 않은 명령. */
  pending: AgentCommandKind | null;
  last_command: { kind: AgentCommandKind; state: string; detail: string | null; at: string } | null;
  agent_behind: boolean;
  agent_wanted: string | null;
}

export interface HubDisplayPage {
  server: { id: string; name: string; can_command: boolean };
  items: HubDisplay[];
  next_cursor: string | null;
}

export interface HubServerInput {
  name: string;
  url: string;
  token: string;
}

/** 통합 관제 연결 토큰 한 줄 — 평문은 만들 때 한 번만 온다(`HubLinkTokenIssued`). 폐기한 것은 오지 않는다. */
export interface HubLinkTokenView {
  id: string;
  name: string;
  role: Role;
  last_used_at: string | null;
  created_at: string;
}

export interface HubLinkTokenIssued {
  id: string;
  name: string;
  role: Role;
  /** 지금 한 번만 보인다. */
  token: string;
}

/** 서버 설치 패키지 — 고객 서버에 USB 로 가져가는 zip. */
export interface ServerPackage {
  id: string;
  version: string;
  /** `addon` 은 이미 깔린 서버에 더하는 적용 묶음(Docker·네이티브 공통). */
  variant: 'docker' | 'native' | 'addon';
  variant_label: string;
  arch: 'x64' | 'arm64';
  arch_label: string;
  /**
   * 용도 — `standard`(사이니지 서버) · `grid`(+ Sentinel 에이전트) · `grid-server`(+ Sentinel 서버·에이전트).
   * 적용 묶음이면 무엇을 더하는지. 옛 서버는 주지 않는다(사이니지 서버).
   */
  edition: 'standard' | 'grid' | 'grid-server';
  /** 용도를 사람 말로 — "사이니지 서버 + Sentinel 에이전트", "설치된 서버에 Sentinel 에이전트 더하기". */
  edition_label: string;
  filename: string;
  size: number;
  /** 서버가 저장소의 바이트로 계산한 값. 확인 전이면 빈 문자열. */
  sha256: string;
  /** `uploading` 이면 아직 내려받을 수 없다. */
  status: 'uploading' | 'ready';
  notes: string;
  created_at: string;
  ready_at: string | null;
}

/** 납품 이력 — 어느 판을 언제 어느 고객사에 납품했나(공급사 계정만). 판·날짜는 고치지 않고 메모만 고친다. */
export interface DeliveryRecord {
  id: string;
  /** YYYY-MM-DD */
  date: string;
  customer: string;
  site: string;
  server_version: string;
  agent_version: string;
  memo: string;
  created_by: string;
  created_at: string;
  updated_by: string;
  updated_at: string;
}

export interface AgentRelease {
  id: string;
  version: string;
  target: 'darwin' | 'windows' | 'linux' | 'android';
  target_label: string;
  arch: 'aarch64' | 'x86_64' | 'arm64-v8a' | 'armeabi-v7a';
  /**
   * 처음 설치용인가, 자동 업데이트용인가.
   *
   * 데스크톱은 둘이 다른 파일이다 — 업데이터는 `.dmg` 를 풀지 못한다. 안드로이드는
   * APK 하나가 둘 다 한다.
   */
  kind: 'installer' | 'update';
  filename: string;
  size: number;
  sha256: string;
  notes: string;
  /**
   * 업데이터 서명이 등록돼 있는가.
   *
   * 없으면 처음 설치는 되지만 **자동 업데이트는 되지 않는다** — 데스크톱 업데이터가
   * 서명을 확인한 뒤에만 설치한다.
   */
  signed: boolean;
  /** 서버가 직접 보관하는가. 아니면 외부 주소로 보낸다. */
  hosted: boolean;
  external_url: string;
  published_at: string;
}

export interface AgentCatalog {
  items: AgentRelease[];
  rollout: { stable_version: string | null; canary_version: string | null };
  install: { base_url: string; shell: string; powershell: string; apk: string };
}

/**
 * 롤아웃 현황.
 *
 * `displays` 가 함께 오는 것이 핵심이다 — 승격은 되돌릴 수 없으므로
 * **몇 대가 대상인지 보고 나서** 누르게 해야 한다. 핀이 걸린 화면은
 * 롤아웃을 올려도 움직이지 않으므로 따로 센다.
 */
/** 무인 업데이트를 까는 시간대 — 화면 현지 시각 `HH:MM`. `end < start` 는 자정을 넘는다. */
export interface InstallWindow {
  start: string;
  end: string;
}

export interface RolloutState {
  stable_version: string | null;
  canary_version: string | null;
  /** 무인 업데이트 설치 시간대(2026-10-19 서버부터). null 이면 언제든. 옛 서버에는 없다. */
  install_window?: InstallWindow | null;
  updated_at: string | null;
  displays: { stable: number; canary: number; pinned: number };
  /** 받아야 할 버전보다 낮은 버전을 돌고 있는 화면 수. 0 이어야 정상이다. 옛 서버에는 없다. */
  behind?: number;
}

export interface InstallTokenRow {
  id: string;
  label: string;
  created_by: string;
  created_at: string;
  expires_at: string;
  expired: boolean;
  use_count: number;
  last_used_at: string | null;
  last_used_ip: string;
}

/** 로그인 화면이 무엇을 보여줄지 정하는 데 필요한 최소 정보. 인증 없이 조회한다. */
export interface AuthState {
  local_accounts: boolean;
  needs_bootstrap: boolean;
  host_identity: boolean;
  /**
   * 사내 계정 로그인(OIDC). 켜져 있으면 단추 글자와, 비밀번호 로그인을 공급사 계정만 쓰게 했는지(`enforce`).
   * 옛 서버는 이 필드가 없다 — 꺼진 것과 같다.
   */
  oidc?: { label: string; enforce: boolean } | null;
}

export interface AccountUser {
  id: string;
  email: string;
  name: string;
  role: Role;
  status: 'active' | 'disabled';
  must_change_password: boolean;
  locked: boolean;
  last_login_at: string | null;
  /** 담당 범위 — 맡은 화면 그룹 id. 비어 있으면 고객사 전체. */
  scope_group_ids: string[];
  /** `oidc` 면 사내 계정 시스템에서 온 계정 — 비밀번호가 없고 역할은 로그인마다 사내 계정 시스템의 값으로 맞춘다. */
  auth_source?: 'local' | 'oidc';
  created_at: string;
}

export interface UserSession {
  id: string;
  current: boolean;
  ip: string;
  user_agent: string;
  created_at: string;
  last_seen_at: string;
  expires_at: string;
}

export interface ReadyReport {
  status: 'ok' | 'degraded';
  checks: {
    database: string;
    /**
     * 오브젝트 스토리지가 응답하는가.
     *
     * **`status` 에는 반영되지 않는다** — 저장소가 죽어도 편성·하트비트는 돌아가므로
     * 서버를 로드밸런서에서 빼지 않는다. 대신 화면이 이 값을 읽어 "저하됨" 으로 말한다.
     */
    storage: string;
    service_enabled: boolean;
    identity_issuers: string[];
  };
}

export interface MetricsReport {
  resolve_cache: { hits: number; misses: number; size: number; hitRate: number };
  uptime_sec: number;
  memory: { rss_mb: number; heap_used_mb: number };
}

/** 리포트 집계 축 — 서버 `services/play-report.ts` 의 `REPORT_AXES`. */
export type ReportAxis = 'asset' | 'display' | 'day' | 'campaign' | 'playlist' | 'location';

export interface ProofOfPlayRow {
  key: string;
  label: string;
  plays: number;
  seconds: number;
  /** 이 줄의 재생이 나온 화면 수. */
  displays: number;
}

export interface ProofOfPlayReport {
  from: string;
  to: string;
  group_by: ReportAxis;
  tz: string;
  /** 기간 전체 — 표가 잘려도 전부다. */
  totals: { plays: number; seconds: number; displays: number; assets: number };
  rows: ProofOfPlayRow[];
  /** 줄이 1,000개를 넘어 위에서부터 잘렸다. */
  truncated: boolean;
}

/* ────────────────────────── 통합 API 스펙 ──────────────────────────
 *
 * 서버가 `/api/v1/public/openapi.json` 으로 내는 문서. 콘솔의 "API 정의" 화면이
 * 이것을 그린다.
 *
 * **콘솔에 엔드포인트 목록을 다시 쓰지 않는다.** 여기 손으로 적어 두면 서버가
 * 엔드포인트를 하나 늘릴 때 콘솔은 모르고, 그 사실이 아무 데서도 드러나지 않는다 —
 * 화면만 조용히 낡는다. 서버가 자기 계약을 말하게 하고 여기서는 그리기만 한다.
 *
 * 필요한 만큼만 타입을 둔다. OpenAPI 전체를 타입으로 옮기면 우리가 쓰지도 않는
 * 필드까지 따라다니게 된다.
 */

export interface OpenApiSchema {
  type?: string;
  format?: string;
  description?: string;
  enum?: unknown[];
  default?: unknown;
  /**
   * 이 값을 그대로 써도 되는 예시.
   *
   * 스키마에서 예시를 **뽑아낼 수 없는** 경우가 있다 — 두 필드가 서로 대안이라
   * 어느 쪽도 필수로 적을 수 없을 때가 그렇다. 그때 서버가 이 값을 실어 준다.
   */
  example?: unknown;
  nullable?: boolean;
  pattern?: string;
  minimum?: number;
  maximum?: number;
  minLength?: number;
  maxLength?: number;
  minItems?: number;
  maxItems?: number;
  required?: string[];
  properties?: Record<string, OpenApiSchema>;
  items?: OpenApiSchema;
  $ref?: string;
}

export interface OpenApiParameter {
  name: string;
  in: 'query' | 'path' | 'header';
  required?: boolean;
  description?: string;
  schema?: OpenApiSchema;
}

export interface OpenApiOperation {
  operationId: string;
  summary: string;
  description?: string;
  /**
   * 이 호출에 필요한 능력. 서버가 구조로 실어 준다.
   *
   * `description` 에도 사람이 읽게 적혀 있지만 그것을 파싱하지 않는다 — 문구를
   * 다듬는 순간 조용히 깨지는 종류의 결합이다.
   */
  'x-capability'?: string | null;
  parameters?: OpenApiParameter[];
  requestBody?: { required?: boolean; content?: Record<string, { schema?: OpenApiSchema }> };
  responses?: Record<string, { description?: string; content?: Record<string, { schema?: OpenApiSchema }> }>;
}

export interface OpenApiDocument {
  openapi: string;
  info: { title: string; version: string; description?: string };
  servers?: Array<{ url: string }>;
  components?: { schemas?: Record<string, OpenApiSchema> };
  paths: Record<string, Record<string, OpenApiOperation>>;
}

/** 호출 실패를 예외로 올린다 — 화면은 try/catch 한 곳에서 오류를 처리한다. */
/** 출하 검사표의 머리말 — 서버는 스네이크 표기로 주고받는다. */
export interface InspectionHeaderRecord {
  doc_no: string;
  customer: string;
  site: string;
  system: string;
  inspected_on: string;
  inspector: string;
  witness: string;
}

/**
 * 서버에 있는 출하 검사표 한 벌. 아무도 저장하지 않았으면 빈 표와 `revision` 0 이다.
 * `revision` 은 저장할 때 그대로 돌려보낸다 — 그 사이 누가 먼저 고쳤으면 409 가 온다.
 */
export interface InspectionRecord {
  header: InspectionHeaderRecord;
  results: Record<string, 'pass' | 'fail' | 'na'>;
  notes: Record<string, string>;
  conclusion: string;
  revision: number;
  updated_at: string | null;
  /** 마지막으로 저장한 주체 — `user:이메일` · `service:토큰이름`. */
  updated_by: string;
}

export type InspectionInput = Pick<InspectionRecord, 'header' | 'results' | 'notes' | 'conclusion' | 'revision'>;

/**
 * 401 이 "로그인이 끊겼다" 가 아닌 요청.
 *
 * 로그인·최초 관리자 만들기의 401 은 "비밀번호가 틀렸다" 이고, `/me` 의 401 은 앱이 로그인
 * 화면을 그릴지 정하려고 **일부러 묻는** 것이다 — 여기서 끊김으로 알리면 신원 확인이 신원
 * 확인을 부르는 고리가 된다.
 */
function isSessionProbe(path: string): boolean {
  return path === '/api/v1/me' || path.startsWith('/api/v1/auth/login') || path.startsWith('/api/v1/auth/bootstrap');
}

/** 쓰는 곳이 있어도 지울 때 — 대신 쓸 것으로 바꾸거나, 쓰는 곳에서 뺀다. */
export type DeleteRelease = { mode: 'replace'; replacementId: string } | { mode: 'detach' };

/** 그래도 지울 때 쓰던 곳을 어떻게 했는가(서버 `services/references.ts` 의 `Released`). */
export interface Released {
  displays: number;
  groups: number;
  directs: number;
  schedules: number;
  campaignsChanged: number;
  campaignsDeleted: number;
}

function releaseQuery(release?: DeleteRelease): string {
  if (!release) return '';
  return release.mode === 'replace'
    ? `?mode=replace&replacement_id=${encodeURIComponent(release.replacementId)}`
    : '?mode=detach';
}

export class ApiCallError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiCallError';
  }
}

export class SignageOneClient {
  constructor(private readonly transport: Transport) {}

  get transportKind(): Transport['kind'] {
    return this.transport.kind;
  }

  mediaUrl(path: string): string {
    return this.transport.mediaUrl(path);
  }

  /**
   * 쓰는 도중 로그인이 끊겼을 때(401) 부른다 — 앱이 신원을 다시 확인해 로그인 화면으로 보낸다.
   *
   * 없으면 화면마다 "인증이 만료되었습니다" 만 띄우고 사람이 새로고침해야 로그인 화면이 나왔다
   * (출하 검사 2.6 불합격). 다른 기기에서 모두 로그아웃했거나 계정이 비활성화되면 여기로 온다.
   */
  onUnauthorized: (() => void) | null = null;

  private unwrap<T>(result: TransportResponse<T>, path: string): T {
    if (!result.ok) {
      if (result.status === 401 && !isSessionProbe(path)) this.onUnauthorized?.();
      throw new ApiCallError(result.error ?? '요청 실패', result.status, result.details);
    }
    return result.data as T;
  }

  private async call<T>(method: string, path: string, body?: unknown): Promise<T> {
    return this.unwrap(await this.transport.request<T>(method, path, body), path);
  }

  // ── 자산
  listAssets(
    params: {
      archived?: string;
      type?: string;
      q?: string;
      tag?: string;
      validity?: string;
      within_days?: string;
      /** `'true'` 면 템플릿으로 등록한 것만. */
      template_entry?: 'true';
      limit?: string;
      cursor?: string;
    } = {},
  ): Promise<ListResponse<Asset>> {
    return this.call('GET', `/api/v1/assets${query(params)}`);
  }
  /** 콘텐츠 전부(거르개 안에서) — 쪽을 끝까지 따라간다. 보관함·담기·고르기는 이것을 쓴다. */
  allAssets(
    params: Omit<Parameters<SignageOneClient['listAssets']>[0], 'limit' | 'cursor'> = {},
  ): Promise<ListResponse<Asset>> {
    return collectPages((cursor) => this.listAssets({ ...params, limit: String(LIST_PAGE), cursor }));
  }
  /**
   * 쓰이고 있는 태그 — 콘텐츠 목록의 태그 드롭다운이 쓴다.
   *
   * 받아 온 페이지에서 추리지 않는다. 콘텐츠가 한 페이지를 넘는 순간 드롭다운에서 태그가
   * 조용히 빠져, 정작 거르기가 필요한 규모에서 틀린다(화면의 `/displays/locations` 와 같다).
   */
  listAssetTags(): Promise<ListResponse<{ name: string; asset_count: number }>> {
    return this.call('GET', '/api/v1/assets/tags');
  }
  /** 다시 변환 — 이미 올린 영상을 화면이 푸는 재생본으로 다시 만든다. 다 될 때까지 원본이 계속 나간다. */
  transcodeAsset(id: string): Promise<Asset> {
    return this.call('POST', `/api/v1/assets/${id}/transcode`);
  }

  /** `reach` 를 주면 배포 현황(`Asset.reach`)까지 받는다 — 화면 배정을 훑는 일이라 물을 때만. */
  /**
   * 올린 HTML 의 글자(2026-10-19) — 미리보기용. 저장소 주소를 받지 않는다: 샌드박스 iframe 의 `srcdoc` 에 넣어 띄운다
   * (`components/HtmlContent.tsx`).
   */
  assetHtml(id: string): Promise<{ id: string; html: string }> {
    return this.call('GET', `/api/v1/assets/${id}/html`);
  }
  /**
   * [화면 편집](2026-10-30) — 올린 HTML 의 내용을 같은 콘텐츠로 바꾼다. 쓰던 편성은 그대로 새 내용을 튼다.
   * `design` 은 편집한 기준 크기 — 주면 그 크기로 굳힌다(늘고 주는 HTML 을 고친 경우).
   */
  saveAssetHtml(id: string, html: string, design?: { width: number; height: number }): Promise<Asset> {
    return this.call('PUT', `/api/v1/assets/${id}/html`, design ? { html, design } : { html });
  }
  /** 이 콘텐츠를 쓰는 플레이리스트의 줄을 다른 콘텐츠로 — 템플릿을 고쳐 만든 HTML 로 갈아 끼울 때. */
  replaceInPlaylists(id: string, replacementId: string): Promise<{ ok: true; playlists: Array<{ id: string; name: string; items: number }> }> {
    return this.call('POST', `/api/v1/assets/${id}/replace-in-playlists`, { replacement_id: replacementId });
  }

  /**
   * 템플릿으로 등록(2026-10-30) — [템플릿 만들기] 의 "등록한 템플릿" 에 나온다. 다시 부르면 이름·설명만 바꾼다. 올린 HTML·템플릿만.
   */
  registerTemplate(id: string, entry: { name: string; description: string }): Promise<Asset> {
    return this.call('PUT', `/api/v1/assets/${id}/template-entry`, entry);
  }
  /** 템플릿 등록을 푼다 — 콘텐츠는 그대로다. */
  unregisterTemplate(id: string): Promise<Asset> {
    return this.call('DELETE', `/api/v1/assets/${id}/template-entry`);
  }

  getAsset(id: string, options: { reach?: boolean } = {}): Promise<Asset> {
    return this.call('GET', `/api/v1/assets/${id}${options.reach ? '?include=reach' : ''}`);
  }
  createWebAsset(input: {
    title: string;
    source_url: string;
    default_dwell_sec?: number;
    tags?: string[];
  }): Promise<Asset> {
    return this.call('POST', '/api/v1/assets/web', input);
  }
  listTemplates(): Promise<ListResponse<TemplateMeta>> {
    return this.call('GET', '/api/v1/templates');
  }
  createTemplateAsset(input: {
    title: string;
    template: TemplatePayload;
    default_dwell_sec?: number;
    tags?: string[];
  }): Promise<Asset> {
    return this.call('POST', '/api/v1/assets/template', input);
  }
  updateTemplateContent(id: string, template: TemplatePayload): Promise<Asset> {
    return this.call('PUT', `/api/v1/assets/${id}/template`, template);
  }
  /** `source_url` 은 웹 콘텐츠만 받는다. */
  updateAsset(
    id: string,
    input: Partial<
      Pick<Asset, 'title' | 'default_dwell_sec' | 'tags' | 'archived' | 'source_url' | 'valid_from' | 'valid_until' | 'volume' | 'html_fit'> & {
        /** 올린 HTML 의 기준 크기 — null 이면 늘고 주는 HTML. 보내면 잰 때가 바뀐다. */
        html_size: { width: number; height: number } | null;
        html_size_manual: boolean;
        /** 적용할 화면 — 이 고객사의 화면 id, null 이면 정하지 않음. */
        html_target_display_id: string | null;
      }
    >,
  ): Promise<Asset> {
    return this.call('PATCH', `/api/v1/assets/${id}`, input);
  }
  /** 여러 콘텐츠의 게시 기간을 한 번에 — 보낸 두 값으로 통째로 바꾼다(null 이면 그쪽 끝 제한 없음). */
  setAssetValidity(input: { asset_ids: string[]; valid_from: string | null; valid_until: string | null }): Promise<{ matched: number; modified: number }> {
    return this.call('POST', '/api/v1/assets/validity', input);
  }
  /**
   * 일괄 삭제 — **부분 실패가 정상이다.**
   *
   * 편성에 물린 자산은 지울 수 없다. 서버가 지운 것과 건너뛴 것을 사유와 함께
   * 돌려주므로, 호출자는 그 둘을 나눠 보여줘야 한다. "몇 개 지웠다" 만 말하면
   * 남아 있는 것을 사용자가 눈으로 세게 된다.
   */
  bulkDeleteAssets(asset_ids: string[]): Promise<{
    deleted: number;
    /** 막은 편성 이름. `schedules` 는 콘텐츠를 직접 가리키는 스케줄 규칙이고, 옛 서버 응답에는 없다. */
    skipped: Array<{ id: string; title: string; reason: string; playlists: string[]; schedules?: string[] }>;
  }> {
    return this.call('POST', '/api/v1/assets/bulk-delete', { asset_ids });
  }
  deleteAsset(id: string): Promise<{ ok: boolean }> {
    return this.call('DELETE', `/api/v1/assets/${id}`);
  }

  // ── 플레이리스트
  /** 한 쪽 — 서버 기본 50개, 최대 200개. 목록·고르기는 `allPlaylists` 를 쓴다. */
  listPlaylists(params: { limit?: string; cursor?: string } = {}): Promise<ListResponse<Playlist>> {
    return this.call('GET', `/api/v1/playlists${query(params)}`);
  }
  /** 플레이리스트 전부 — 쪽을 끝까지 따라간다. */
  allPlaylists(): Promise<ListResponse<Playlist>> {
    return collectPages((cursor) => this.listPlaylists({ limit: String(LIST_PAGE), cursor }));
  }
  getPlaylist(id: string): Promise<Playlist> {
    return this.call('GET', `/api/v1/playlists/${id}`);
  }
  createPlaylist(input: {
    name: string;
    description?: string;
    items?: PlaylistItem[];
    default_transition?: Transition;
  }): Promise<Playlist> {
    return this.call('POST', '/api/v1/playlists', input);
  }
  updatePlaylist(id: string, input: Partial<{ name: string; description: string; items: PlaylistItem[] }>): Promise<Playlist> {
    return this.call('PATCH', `/api/v1/playlists/${id}`, input);
  }
  /**
   * 지운다. 쓰는 곳이 있으면 409 와 그 목록이 온다 — `release` 를 주면 그래도 지운다: 쓰던 곳을 대신 쓸 것으로
   * 바꾸거나(replace) 쓰는 곳에서 빼고(detach). 나가고 있는 긴급이 쓰면 어느 쪽도 409 다.
   */
  deletePlaylist(id: string, release?: DeleteRelease): Promise<{ ok: boolean; released?: Released }> {
    return this.call('DELETE', `/api/v1/playlists/${id}${releaseQuery(release)}`);
  }

  // ── 화면
  listDisplays(
    params: {
      status?: string;
      q?: string;
      group_id?: string;
      /** 위치로 거르기. **빈 문자열은 "위치 미지정"** 이고, 빼면 전체다. */
      location?: string;
      hidden?: 'exclude' | 'include' | 'only';
      /**
       * 한 번에 받을 수. 서버 기본은 50, 최대 200.
       *
       * **세어서 보여주는 화면은 이것을 반드시 정해야 한다.** 기본값으로 받아 놓고
       * 그 안에서 집계하면, 화면이 50대를 넘는 순간 숫자가 조용히 틀린다 — 대시보드의
       * 위치별 집계가 실제로 그랬다.
       */
      limit?: string;
      cursor?: string;
    } = {},
  ): Promise<ListResponse<Display>> {
    /*
     * `location` 만 따로 싣는다. 이 파라미터는 **빈 값이 "위치 미지정" 이라는 뜻**인데,
     * 공용 `query()` 는 빈 값을 버린다(대개는 그게 맞다 — 빈 검색어를 보낼 이유가 없다).
     */
    const { location, ...rest } = params;
    const search = query(rest);
    const locationPart =
      location === undefined ? '' : `${search ? '&' : '?'}location=${encodeURIComponent(location)}`;
    return this.call('GET', `/api/v1/displays${search}${locationPart}`);
  }
  /** 화면 전부 — 쪽을 끝까지 따라간다(`collectPages`). 목록·집계·고르기는 이것을 쓴다. */
  allDisplays(params: Omit<Parameters<SignageOneClient['listDisplays']>[0], 'limit' | 'cursor'> = {}): Promise<ListResponse<Display>> {
    return collectPages((cursor) => this.listDisplays({ ...params, limit: String(LIST_PAGE), cursor }));
  }
  /**
   * 목록이 따라가는 지금 상태 — `allDisplays` 와 **같은 거르개**를 넘긴다. 쪽 없이 한 번에 온다.
   *
   * 목록 전체를 30초마다 다시 받는 대신 이것을 받아 받아 둔 줄에 얹는다. 줄이 생기거나 사라지면
   * (`mergeDisplayStatus` 가 null) 그때만 목록째 다시 받는다.
   */
  displaysStatus(
    params: Omit<NonNullable<Parameters<SignageOneClient['listDisplays']>[0]>, 'limit' | 'cursor'> = {},
  ): Promise<DisplayStatusResponse> {
    const { location, ...rest } = params;
    const search = query(rest);
    const locationPart =
      location === undefined ? '' : `${search ? '&' : '?'}location=${encodeURIComponent(location)}`;
    return this.call('GET', `/api/v1/displays/status${search}${locationPart}`);
  }
  /**
   * 이 편성이 이 화면들의 저장 공간에 들어가는가 — 배정·바로 보내기 전의 경고. 아무것도 바꾸지 않는다.
   * 범위 밖·없는 화면은 결과에서 빠진다.
   */
  storageCheck(input: StorageCheckInput): Promise<StorageCheckResult> {
    return this.call('POST', '/api/v1/displays/storage-check', input);
  }
  /** 화면 월 — 한 번의 호출로 모든 화면의 지금 상태를 받는다. */
  wall(
    params: {
      status?: string;
      q?: string;
      limit?: string;
      hidden?: 'exclude' | 'include' | 'only';
      /** 이 조회로 화면들에게 자기 화면을 찍으라고 할 것인가. 그림을 다 집었으면 'false'. */
      capture?: 'true' | 'false';
    } = {},
  ): Promise<WallResponse> {
    return this.call('GET', `/api/v1/displays/wall${query(params)}`);
  }

  fleetSummary(options: { locations?: boolean } = {}): Promise<FleetSummary> {
    return this.call('GET', `/api/v1/displays/summary${options.locations ? '?locations=1' : ''}`);
  }
  /** 쓰이고 있는 위치 목록. 위치 드롭다운이 고를 값이다. */
  listLocations(): Promise<ListResponse<DisplayLocation>> {
    return this.call('GET', '/api/v1/displays/locations');
  }
  /** 화면에 붙어 있는 태그 목록. 화면 태그 드롭다운이 고를 값이다 — 위치 목록과 같은 규칙. */
  listDisplayTags(): Promise<ListResponse<{ name: string; display_count: number }>> {
    return this.call('GET', '/api/v1/displays/tags');
  }
  /**
   * 위치 이름 바꾸기 — 그 이름을 쓰는 화면 전부(숨긴 화면 포함)와 사업장 구성·계정별 배치를 함께 옮긴다.
   * 바꿀 이름이 이미 있으면 합쳐진다(`merged`). 담당 범위가 있는 계정은 403.
   */
  renameLocation(from: string, to: string): Promise<LocationChangeResult> {
    return this.call('POST', '/api/v1/displays/locations/rename', { from, to });
  }
  /** 위치 지우기 — 그 이름의 화면을 "위치 미지정" 으로 돌린다. 화면은 지우지 않는다. */
  deleteLocation(name: string): Promise<LocationChangeResult> {
    return this.call('POST', '/api/v1/displays/locations/delete', { name });
  }
  /**
   * 모니터링 한 판의 배치 — **위치 카드와 그 안의 화면.** 계정별이다.
   *
   * 판은 가로 24칸이고 세로 칸도 같은 크기다(정사각 셀). 위치 카드는 판 위에서, 화면은 그
   * 위치 카드 안쪽 판(다시 24칸)에서 자리(`x`·`y`)와 크기(`w`·`h`)를 갖는다. 카드는 놓은
   * 자리에 그대로 머물고 서로 겹치지 않는다.
   *
   * 정한 적이 없으면 빈 배열이 오고, 그때 콘솔은 위치 이름순으로 카드를 세운다.
   */
  /**
   * 사업장 그림에서 옮기거나 키운 영역(지역·공장·건물) — 계정별, 부모 상자 기준 px. 정한 적 없으면 빈 배열(자동 배치).
   */
  siteLayout(): Promise<{ items: SiteLayoutItem[] }> {
    return this.call('GET', '/api/v1/me/site-layout');
  }
  /** 통째로 바꾼다 — 빈 배열이면 기본 배치. */
  saveSiteLayout(items: ReadonlyArray<SiteLayoutItem>): Promise<{ items: SiteLayoutItem[] }> {
    return this.call('POST', '/api/v1/me/site-layout', { items });
  }
  wallLayout(): Promise<{ places: WallPlace[] }> {
    return this.call('GET', '/api/v1/me/wall-layout');
  }
  /** 판을 저장한다. 보낸 것으로 통째로 바뀐다. */
  saveWallLayout(input: { places: ReadonlyArray<WallPlace> }): Promise<{ places: WallPlace[] }> {
    return this.call('POST', '/api/v1/me/wall-layout', input);
  }
  /** 출하 검사표 — 시스템 계정들이 함께 보는 한 벌. */
  getInspection(): Promise<InspectionRecord> {
    return this.call('GET', '/api/v1/inspection');
  }
  /** 통째로 저장한다. 읽은 뒤 누가 먼저 고쳤으면 409 이고, `details` 에 지금 서버의 표가 온다. */
  saveInspection(input: InspectionInput): Promise<InspectionRecord> {
    return this.call('PUT', '/api/v1/inspection', input);
  }
  getDisplay(id: string): Promise<Display> {
    return this.call('GET', `/api/v1/displays/${id}`);
  }
  previewDisplay(id: string, at?: string): Promise<DisplayPreview> {
    return this.call('GET', `/api/v1/displays/${id}/preview${at ? `?at=${encodeURIComponent(at)}` : ''}`);
  }
  /**
   * 이 화면이 로컬에 들고 있는 콘텐츠 파일.
   *
   * **조회 자체가 "보고 있다"는 신호다.** 화면은 그 신호를 받은 뒤에야 목록을 세어
   * 올리므로, 처음 열었을 때 비어 있는 것은 정상이다 — 한 주기(폴링 30초 + 캐시 틱)
   * 뒤에 찬다. 목록을 세는 일이 파일 수백 개를 훑는 일이라, 아무도 보지 않는 동안
   * 켜 두면 그 비용이 재생 품질로 돌아온다.
   */
  displayCache(id: string): Promise<DisplayCache> {
    return this.call('GET', `/api/v1/displays/${id}/cache`);
  }
  /**
   * 들고 있는 파일을 지우라고 지시한다.
   *
   * **곧바로 지워지지 않는다.** 서버에서 화면으로 가는 채널은 편성 폴링뿐이라 명령을
   * 밀어 넣을 수 없다. 지시를 적어 두고 다음 폴링에 실어 보내며, 화면이 지웠다고
   * 보고하면 그것으로 지운다. 꺼져 있는 화면은 다시 켜질 때 수행하고, 하루가 지나면
   * 지시 자체가 만료된다.
   *
   * 지금 편성에 쓰이는 파일도 지울 수 있다 — 화면이 지운 뒤 곧바로 다시 받으므로,
   * 그 조합이 곧 "받다 만 파일 강제 새로고침"이다.
   */
  evictDisplayCache(
    id: string,
    asset_ids: string[],
  ): Promise<{ ok: boolean; pending: string[]; online: boolean }> {
    // 꺼진 화면은 409 `display_offline` — 지우기는 화면이 직접 하는 일이다.
    return this.call('POST', `/api/v1/displays/${id}/cache/evict`, { asset_ids });
  }
  updateDisplay(id: string, input: Partial<Pick<Display, 'name' | 'location' | 'region' | 'timezone' | 'tags' | 'orientation' | 'rotation' | 'group_id' | 'assignment' | 'channel' | 'pinned_version' | 'hidden' | 'hours' | 'audio'>>): Promise<Display> {
    return this.call('PATCH', `/api/v1/displays/${id}`, input);
  }
  bulkUpdateDisplays(input: {
    display_ids: string[];
    assignment?: Assignment;
    add_tags?: string[];
    remove_tags?: string[];
    group_id?: string | null;
    channel?: Display['channel'];
    hidden?: boolean;
    /** 여러 대를 한 위치로. 빈 문자열은 "위치 미지정"으로 되돌린다. */
    location?: string;
    /** 소리 — 보낸 것만 바꾼다(음소거만 걸면 화면마다의 음량은 그대로). */
    audio?: Partial<{ volume: number; muted: boolean }>;
  }): Promise<{ matched: number; modified: number }> {
    return this.call('POST', '/api/v1/displays/bulk', input);
  }
  /**
   * 정비 모드 PIN 을 다시 정한다. 한 대든 여러 대든 같은 경로다.
   *
   * `pin: null` 은 콘솔 PIN 을 지워 화면 기기 설정으로 돌린다. 화면 관리 권한(`fleet:write`)이
   * 필요하다 — 편성 담당이 쓰는 `updateDisplay` 와 나눈 이유다.
   */
  setMaintenancePin(display_ids: string[], pin: string | null): Promise<{ ok: boolean; matched: number; modified: number }> {
    return this.call('POST', '/api/v1/displays/maintenance-pin', { display_ids, pin });
  }
  /**
   * 원격 설정(자동 업데이트·부팅 시 자동 실행)을 정한다. 보낸 값만 바뀌고, null 은 기기에 맡긴다.
   * 화면 관리 권한(`fleet:write`)이 필요하다.
   */
  setAgentSettings(
    display_ids: string[],
    change: { auto_update?: boolean | null; autostart?: boolean | null },
  ): Promise<{ ok: boolean; matched: number; modified: number }> {
    return this.call('POST', '/api/v1/displays/agent-settings', { display_ids, ...change });
  }
  /**
   * 화면에 원격 명령을 건다 — 재부팅·앱 종료·전원 종료. 켜져 있고 그 명령을 아는(할 수 있는)
   * 화면에만 걸린다(아니면 409). 같은 명령이 걸려 있으면 그것을 돌려준다(`already`).
   */
  sendDisplayCommand(
    id: string,
    kind: AgentCommandKind,
  ): Promise<{ ok: boolean; already: boolean; agent_control: AgentControl }> {
    return this.call('POST', `/api/v1/displays/${id}/command`, { kind });
  }
  /** 기기 로그를 청한다 — 그 화면을 깨워 곧바로 받게 한다. 걸려 있으면 그것을 돌려준다. */
  requestDisplayLogs(id: string): Promise<{ ok: boolean; request: { id: string; requested_at: string }; online: boolean }> {
    return this.call('POST', `/api/v1/displays/${id}/logs/request`, {});
  }
  /** 받은 기기 로그(최근 20벌)와 걸려 있는 요청. */
  displayLogs(id: string): Promise<{ pending: { id: string; requested_at: string } | null; items: DeviceLogEntry[] }> {
    return this.call('GET', `/api/v1/displays/${id}/logs`);
  }
  /** 기기 로그 본문 주소 — 링크로 내려받는다(세션 쿠키가 함께 간다). */
  displayLogUrl(id: string, logId: string): string {
    return this.mediaUrl(`/api/v1/displays/${id}/logs/${logId}`);
  }
  /** 여러 화면에 같은 명령 — 걸 수 있는 화면에만 걸고, 나머지는 까닭과 함께 돌려준다(전부 거절하지 않는다). */
  bulkDisplayCommand(display_ids: string[], kind: AgentCommandKind): Promise<BulkCommandResult> {
    return this.call('POST', '/api/v1/displays/bulk-command', { display_ids, kind });
  }
  deleteDisplay(id: string): Promise<{ ok: boolean }> {
    return this.call('DELETE', `/api/v1/displays/${id}`);
  }
  /**
   * 일괄 삭제 — **되돌릴 수 없다.**
   *
   * 화면 기록이 지워지고 디바이스 토큰이 폐기되어 그 기기는 다시 페어링해야 한다.
   * 목록 정리가 목적이라면 `bulkUpdateDisplays({ hidden: true })` 를 쓴다.
   */
  bulkDeleteDisplays(display_ids: string[]): Promise<{ ok: boolean; deleted: number }> {
    return this.call('POST', '/api/v1/displays/bulk-delete', { display_ids });
  }
  /** 이 화면에 지금 띄울 것을 보낸다. 편성은 그대로 두고 그 위에 얹는다. */
  sendDirect(
    displayId: string,
    /** 콘텐츠(`asset_ids`) 또는 플레이리스트(`playlist_id`, 2026-10-28 — 보내는 순간의 켜 둔 항목 그대로) 중 하나. */
    input: { asset_ids?: string[]; playlist_id?: string; dwell_sec?: number; until?: string | null; note?: string; queue_offline?: boolean },
  ): Promise<{ display: Display }> {
    return this.call('POST', `/api/v1/displays/${displayId}/direct`, input);
  }

  clearDirect(displayId: string): Promise<{ display: Display }> {
    return this.call('DELETE', `/api/v1/displays/${displayId}/direct`);
  }

  /**
   * 켜진 화면에만 보낸다 — 오프라인이라 뺀 화면은 `skipped`. 모두 꺼져 있으면 409 `display_offline`.
   * `queue_offline` 이면 꺼진 화면에도 걸어 두고 그 화면들을 `queued` 로 돌려준다.
   */
  sendDirectBulk(input: {
    display_ids: string[];
    asset_ids?: string[];
    playlist_id?: string;
    dwell_sec?: number;
    until?: string | null;
    note?: string;
    queue_offline?: boolean;
  }): Promise<{ updated: number; skipped: Array<{ id: string; name: string }>; queued?: Array<{ id: string; name: string }> }> {
    return this.call('POST', '/api/v1/displays/direct', input);
  }

  /**
   * 파일을 올려 자산으로 만든다.
   *
   * multipart 라 JSON 전송 경로를 쓰지 않는다. 업로드 경로를 하나로 두어야
   * 중복 제거·형식 판정이 한 곳에서만 지켜진다.
   */
  async uploadAsset(file: File, tags: readonly string[] = [], title?: string): Promise<Asset> {
    const form = new FormData();
    form.set('file', file);
    // 확장자를 뗀 파일 이름을 제목으로. 목록에서 알아볼 수 있으면 충분하다(PDF 쪽 그림은 `안전수칙.pdf (1/12)` 처럼 따로 준다).
    form.set('title', title ?? file.name.replace(/\.[^.]+$/, ''));
    // 태그는 쉼표로 한 줄에 싣는다 — multipart 는 JSON 스키마를 지나지 않는다(서버가 같은 한도로 나눈다).
    if (tags.length > 0) form.set('tags', tags.join(','));

    const token = (globalThis as { __SIGNAGEONE_TOKEN__?: string }).__SIGNAGEONE_TOKEN__;
    const response = await fetch(this.mediaUrl('/api/v1/assets/upload'), {
      method: 'POST',
      body: form,
      headers: token ? { authorization: `Bearer ${token}` } : {},
      credentials: 'include',
    });
    const body = (await response.json().catch(() => null)) as (Asset & { message?: string }) | null;
    if (!response.ok) {
      throw new ApiCallError(body?.message ?? `업로드 실패 (${response.status})`, response.status);
    }
    return body as Asset;
  }

  claimPairing(input: { code: string; name?: string; location?: string; tags?: string[] }): Promise<{ display: { id: string; name: string } }> {
    return this.call('POST', '/api/v1/pairing/claim', input);
  }

  /**
   * 등록을 기다리는 화면들.
   *
   * `listable: false` 는 실패가 아니다 — 여러 조직이 한 서버를 쓰면 서버가 목록을 내주지
   * 않는다(이유는 `routes/pairing.ts`). 그때는 코드를 직접 입력한다.
   */
  listPendingPairings(): Promise<{ listable: boolean; items: PendingPairing[] }> {
    return this.call('GET', '/api/v1/pairing/pending');
  }

  // ── 그룹
  //
  // 그룹은 화면 묶음이다 — 배정이 없는 화면에 편성을 물려주고, 캠페인·긴급의 대상이 되고, 계정의 담당 범위가 된다.
  // 화면을 넣고 빼는 것은 그룹이 아니라 화면 쪽 `group_id` 다(`updateDisplay`·`bulkUpdateDisplays`), 들어 있는
  // 화면은 `listDisplays({ group_id })` 로 받는다.
  //
  // 만들기·지우기는 담당 범위가 없는 계정만 된다(서버 403) — 범위가 있는 사람이 만든 그룹은 만들자마자 그 사람의
  // 범위 밖이고, 지우면 그 그룹만 맡던 계정이 고객사 전체를 맡게 될 수 있다.
  listGroups(): Promise<ListResponse<Group>> {
    return this.call('GET', '/api/v1/groups');
  }
  createGroup(input: { name: string; assignment?: Assignment }): Promise<{ id: string; name: string }> {
    return this.call('POST', '/api/v1/groups', input);
  }
  /** 이름이나 그룹 편성을 바꾼다. 같은 이름의 그룹이 있으면 409, 담당 범위 밖의 그룹이면 404. */
  updateGroup(id: string, input: Partial<{ name: string; assignment: Assignment }>): Promise<{ ok: boolean }> {
    return this.call('PATCH', `/api/v1/groups/${id}`, input);
  }
  /** 그룹을 지운다. 쓰이고 있으면 409 — 무엇이 막는지는 `groupDeleteBlockers` 로 읽는다. */
  deleteGroup(id: string): Promise<{ ok: boolean }> {
    return this.call('DELETE', `/api/v1/groups/${id}`);
  }

  // ── 스케줄
  listSchedules(): Promise<ListResponse<Schedule>> {
    return this.call('GET', '/api/v1/schedules');
  }
  createSchedule(input: {
    name: string;
    timezone?: string;
    default_playlist_id?: string | null;
    default_asset_id?: string | null;
    rules?: ScheduleRule[];
  }): Promise<Schedule> {
    return this.call('POST', '/api/v1/schedules', input);
  }
  updateSchedule(
    id: string,
    input: Partial<{
      name: string;
      timezone: string;
      default_playlist_id: string | null;
      default_asset_id: string | null;
      rules: ScheduleRule[];
    }>,
  ): Promise<Schedule> {
    return this.call('PATCH', `/api/v1/schedules/${id}`, input);
  }
  deleteSchedule(id: string, release?: DeleteRelease): Promise<{ ok: boolean; released?: Released }> {
    return this.call('DELETE', `/api/v1/schedules/${id}${releaseQuery(release)}`);
  }
  previewSchedule(id: string, date: string, step = 15): Promise<SchedulePreview> {
    return this.call('GET', `/api/v1/schedules/${id}/preview?date=${date}&step=${step}`);
  }

  // ── 캠페인 · 긴급
  listCampaigns(): Promise<ListResponse<Campaign>> {
    return this.call('GET', '/api/v1/campaigns');
  }
  createCampaign(input: Record<string, unknown>): Promise<Campaign> {
    return this.call('POST', '/api/v1/campaigns', input);
  }
  updateCampaign(id: string, input: Record<string, unknown>): Promise<Campaign> {
    return this.call('PATCH', `/api/v1/campaigns/${id}`, input);
  }
  deleteCampaign(id: string): Promise<{ ok: boolean }> {
    return this.call('DELETE', `/api/v1/campaigns/${id}`);
  }
  listEmergencies(): Promise<ListResponse<Emergency>> {
    return this.call('GET', '/api/v1/emergencies');
  }
  /** 긴급 발령 — 플레이리스트를 덮어 틀거나(`playlist_id`), 화면을 비운다(`blank: true`). */
  createEmergency(input: {
    name: string;
    playlist_id?: string | null;
    blank?: boolean;
    target:
      | Campaign['target']
      | { kind: 'all' }
      | { kind: 'tags'; tags: string[] }
      | { kind: 'groups'; group_ids: string[] };
    end_at?: string | null;
  }): Promise<Emergency> {
    return this.call('POST', '/api/v1/emergencies', input);
  }
  clearEmergency(id: string): Promise<{ ok: boolean }> {
    return this.call('POST', `/api/v1/emergencies/${id}/clear`);
  }

  // ── 리포트
  proofOfPlay(params: { from: string; to: string; group_by?: string; display_id?: string; tz?: string }): Promise<ProofOfPlayReport> {
    return this.call('GET', `/api/v1/reports/proof-of-play${query(params)}`);
  }

  // ── 인증 · 계정
  authState(): Promise<AuthState> {
    return this.call('GET', '/api/v1/auth/state');
  }

  bootstrapAccount(input: { email: string; name: string; password: string }): Promise<{ user: AccountUser }> {
    return this.call('POST', '/api/v1/auth/bootstrap', input);
  }

  login(input: { email: string; password: string }): Promise<{ user: AccountUser }> {
    return this.call('POST', '/api/v1/auth/login', input);
  }

  logout(): Promise<{ ok: boolean }> {
    return this.call('POST', '/api/v1/auth/logout');
  }

  changePassword(input: { current_password: string; new_password: string }): Promise<{ ok: boolean }> {
    return this.call('POST', '/api/v1/auth/change-password', input);
  }

  listSessions(): Promise<ListResponse<UserSession>> {
    return this.call('GET', '/api/v1/auth/sessions');
  }

  revokeOtherSessions(): Promise<{ revoked: number }> {
    return this.call('POST', '/api/v1/auth/sessions/revoke-others');
  }

  // ── 사용자 관리(관리자)
  listUsers(): Promise<ListResponse<AccountUser>> {
    return this.call('GET', '/api/v1/users');
  }

  createUser(input: {
    email: string;
    name: string;
    role: Role;
    password?: string;
    scope_group_ids?: string[];
  }): Promise<{ user: AccountUser; temporary_password: string | null }> {
    return this.call('POST', '/api/v1/users', input);
  }

  updateUser(
    id: string,
    input: Partial<{ name: string; role: Role; status: 'active' | 'disabled'; scope_group_ids: string[] }>,
  ): Promise<{ user: AccountUser }> {
    return this.call('PATCH', `/api/v1/users/${id}`, input);
  }

  resetUserPassword(id: string): Promise<{ temporary_password: string }> {
    return this.call('POST', `/api/v1/users/${id}/reset-password`);
  }

  deleteUser(id: string): Promise<{ ok: boolean }> {
    return this.call('DELETE', `/api/v1/users/${id}`);
  }

  // ── 납품 이력(공급사 계정만)
  listDeliveries(): Promise<{ items: DeliveryRecord[]; memo_max: number }> {
    return this.call('GET', '/api/v1/admin/deliveries');
  }
  createDelivery(input: {
    date: string;
    customer: string;
    site?: string;
    server_version: string;
    agent_version: string;
    memo?: string;
  }): Promise<DeliveryRecord> {
    return this.call('POST', '/api/v1/admin/deliveries', input);
  }
  /** 메모만 고친다 — 판·날짜는 이력이라 서버가 받지 않는다. */
  updateDeliveryMemo(id: string, memo: string): Promise<DeliveryRecord> {
    return this.call('PATCH', `/api/v1/admin/deliveries/${id}`, { memo });
  }

  // ── 서버 설치 패키지
  listServerPackages(): Promise<{ items: ServerPackage[]; max_bytes: number }> {
    return this.call('GET', '/api/v1/admin/server-packages');
  }

  /** 올릴 자리를 만든다. 돌려받은 주소로 파일을 **저장소에 직접** PUT 한 뒤 `completeServerPackage`. */
  createServerPackage(input: {
    filename: string;
    size: number;
    notes?: string;
  }): Promise<{ package: ServerPackage; upload: { method: 'PUT'; url: string; expires_at: string } }> {
    return this.call('POST', '/api/v1/admin/server-packages', input);
  }

  /** 다 올렸다 — 서버가 크기를 대조하고 SHA-256 을 계산한다(수백 MB 면 몇 초 걸린다). */
  completeServerPackage(id: string, sha256?: string): Promise<{ package: ServerPackage }> {
    return this.call('POST', `/api/v1/admin/server-packages/${id}/complete`, sha256 ? { sha256 } : {});
  }

  /** 내려받을 단명 주소. 누를 때마다 새로 받는다 — 목록을 띄워 둔 사이에 만료되지 않게. */
  serverPackageDownload(
    id: string,
  ): Promise<{ url: string; filename: string; size: number; sha256: string; expires_at: string }> {
    return this.call('GET', `/api/v1/admin/server-packages/${id}/download`);
  }

  deleteServerPackage(id: string): Promise<{ ok: boolean }> {
    return this.call('DELETE', `/api/v1/admin/server-packages/${id}`);
  }

  // ── 에이전트 배포
  agentCatalog(): Promise<AgentCatalog> {
    return this.call('GET', '/api/v1/agent/releases');
  }

  listInstallTokens(): Promise<ListResponse<InstallTokenRow>> {
    return this.call('GET', '/api/v1/agent/install-tokens');
  }

  /** 발급. 평문 토큰과 짧은 설치 코드(`K7QM-4XPA`)는 이 응답에만 있다 — 서버는 해시만 남긴다. */
  createInstallToken(label: string): Promise<{ id: string; token: string; code: string; expires_at: string }> {
    return this.call('POST', '/api/v1/agent/install-tokens', { label });
  }

  revokeInstallToken(id: string): Promise<{ ok: boolean }> {
    return this.call('DELETE', `/api/v1/agent/install-tokens/${id}`);
  }

  deleteRelease(id: string): Promise<{ ok: boolean }> {
    return this.call('DELETE', `/api/v1/agent/releases/${id}`);
  }

  /** 롤아웃 현황. `admin` 만 볼 수 있다 — 채널별 화면 수가 함께 온다. */
  rollout(): Promise<RolloutState> {
    return this.call('GET', '/api/v1/admin/rollout');
  }

  /**
   * 채널에 버전을 지정한다. `null` 이면 그 채널의 배포를 멈춘다.
   *
   * 등록되지 않은 버전은 서버가 400 으로 막는다 — 승격은 됐는데 산출물이 없어
   * 업데이트가 조용히 204 로 떨어지는 사고를 여기서 끊는다.
   */
  setRollout(input: {
    stable_version?: string | null;
    canary_version?: string | null;
    /** 무인 업데이트 설치 시간대(화면 현지 시각). null 이면 언제든. */
    install_window?: InstallWindow | null;
  }): Promise<{ ok: boolean }> {
    return this.call('POST', '/api/v1/admin/rollout', input);
  }

  /**
   * 산출물을 받아 파일로 저장한다.
   *
   * 평범한 `<a href>` 로 두지 않는 이유: 서비스 토큰으로 붙은 콘솔은 헤더에
   * 인증을 실어야 하는데 링크 이동에는 헤더를 붙일 수 없다. 세션 쿠키든
   * Bearer 든 같은 코드로 동작하게 fetch 로 받아 blob 으로 저장한다.
   */
  async downloadRelease(release: AgentRelease): Promise<void> {
    const token = (globalThis as { __SIGNAGEONE_TOKEN__?: string }).__SIGNAGEONE_TOKEN__;
    const response = await fetch(this.mediaUrl(`/api/v1/agent/download/${release.id}`), {
      headers: token ? { authorization: `Bearer ${token}` } : {},
      credentials: 'include',
    });
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as { message?: string } | null;
      throw new ApiCallError(body?.message ?? `내려받기 실패 (${response.status})`, response.status);
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = release.filename || 'signageone-agent';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  /**
   * 백업 파일 내려받기.
   *
   * **세션 쿠키로 붙은 콘솔은 링크로 받는다.** 자산까지 담으면 수 GB 가 되는데, fetch 로 받으면
   * 그 전부가 브라우저 메모리에 한 번 올라갔다가 저장된다 — 큰 설치에서는 그 자리에서 탭이 죽는다.
   * 링크는 브라우저가 디스크로 바로 흘린다. 헤더를 실어야 하는 토큰 연결에서만 fetch 로 받는다.
   */
  async downloadBackup(includeMedia: boolean): Promise<void> {
    const path = `/api/v1/admin/backup?media=${includeMedia ? '1' : '0'}`;
    const token = (globalThis as { __SIGNAGEONE_TOKEN__?: string }).__SIGNAGEONE_TOKEN__;
    if (!token) {
      window.open(this.mediaUrl(path), '_blank', 'noopener');
      return;
    }

    const response = await fetch(this.mediaUrl(path), {
      headers: { authorization: `Bearer ${token}` },
      credentials: 'include',
    });
    if (!response.ok) {
      const body = (await response.json().catch(() => null)) as { message?: string } | null;
      throw new ApiCallError(body?.message ?? `백업 내려받기 실패 (${response.status})`, response.status);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = backupFilename(response.headers.get('content-disposition'));
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  // ── 변경 이력 — 플레이리스트·스케줄·캠페인·템플릿
  history(type: HistoryType, id: string): Promise<{ items: HistoryEntry[] }> {
    return this.call('GET', `/api/v1/history/${type}/${id}`);
  }
  /** 그 판으로 되돌린다 — 평소 수정과 같은 검사를 지나고, 되돌린 것도 새 판이 된다. */
  restoreRevision(type: HistoryType, id: string, version: number): Promise<unknown> {
    return this.call('POST', `/api/v1/history/${type}/${id}/${version}/restore`);
  }

  // ── 환경 설정 (고객사 — `settings:write`)
  settings(): Promise<TenantSettings> {
    return this.call('GET', '/api/v1/settings');
  }
  /** 보낸 칸만 바꾼다. `revision` 은 읽은 판 그대로. */
  saveSettings(patch: TenantSettingsPatch): Promise<TenantSettings> {
    return this.call('PATCH', '/api/v1/settings', patch);
  }
  /** 사내 글꼴 올리기 — 예전 파일을 바꾼다. 판이 오르므로 돌려받은 설정으로 바꿔 끼운다. */
  async uploadBrandFont(file: File): Promise<TenantSettings> {
    const form = new FormData();
    form.set('file', file);
    const token = (globalThis as { __SIGNAGEONE_TOKEN__?: string }).__SIGNAGEONE_TOKEN__;
    const response = await fetch(this.mediaUrl('/api/v1/settings/brand-font'), {
      method: 'POST',
      body: form,
      headers: token ? { authorization: `Bearer ${token}` } : {},
      credentials: 'include',
    });
    const body = (await response.json().catch(() => null)) as (TenantSettings & { message?: string }) | null;
    if (!response.ok) {
      throw new ApiCallError(body?.message ?? `글꼴 올리기 실패 (${response.status})`, response.status);
    }
    return body as TenantSettings;
  }
  deleteBrandFont(): Promise<TenantSettings> {
    return this.call('DELETE', '/api/v1/settings/brand-font');
  }

  // ── 장애 감시
  listAlerts(status: 'firing' | 'resolved' | 'all' = 'all'): Promise<AlertList> {
    return this.call('GET', `/api/v1/admin/alerts?status=${status}&limit=100`);
  }
  checkAlerts(): Promise<{ results: AlertCheckResult[] }> {
    return this.call('POST', '/api/v1/admin/alerts/check');
  }

  // ── 통합 관제 연결 (환경 설정 권한) — 다른 서버의 통합 관제가 이 서버를 부를 때 쓰는 토큰
  listHubLinkTokens(): Promise<{ items: HubLinkTokenView[] }> {
    return this.call('GET', '/api/v1/hub/link-tokens');
  }
  createHubLinkToken(): Promise<HubLinkTokenIssued> {
    return this.call('POST', '/api/v1/hub/link-tokens');
  }
  revokeHubLinkToken(id: string): Promise<{ ok: true }> {
    return this.call('DELETE', `/api/v1/hub/link-tokens/${encodeURIComponent(id)}`);
  }

  // ── 사업장 구성 (모니터링 "사업장 현황" — 지역 › 사업장 › 위치의 이름표, 층의 이름은 고객사가 고른다)
  sites(): Promise<SiteMapView> {
    return this.call('GET', '/api/v1/sites');
  }
  /**
   * 통째로 바꾼다. 읽었던 `revision` 과 다르면 409 와 지금 표. 지역·사업장이 다 빈 줄은 서버가 버린다.
   * 조직 이름(`name`)과 층의 이름(`labels`)은 보낼 때만 바뀐다.
   */
  saveSites(input: {
    name?: string;
    labels?: { region: string; site: string; location: string };
    entries: SiteEntry[];
    revision: number;
  }): Promise<SiteMapView> {
    return this.call('PUT', '/api/v1/sites', input);
  }

  // ── 주소 DB (행정안전부 도로명주소·위치정보요약DB 를 서버에 넣고 그 안에서 찾는다 — 인터넷 없이)
  addressDbStatus(): Promise<AddressDbStatus> {
    return this.call('GET', '/api/v1/address-db/status');
  }
  searchAddress(q: string, limit = 20): Promise<AddressSearchResponse> {
    return this.call('GET', `/api/v1/address-db/search?${new URLSearchParams({ q, limit: String(limit) })}`);
  }
  /**
   * 주소정보누리집에서 받은 zip 하나를 올린다 — 넣기는 서버 뒤에서 돌고 `addressDbStatus` 로 진행을 본다(시스템 역할).
   * 공개 주소가 Cloudflare 를 지나면 100MB 를 넘는 zip(위치정보요약DB 전체분 152MB)은 막힌다 — 사내 주소로 연다.
   */
  async importAddressDb(file: File): Promise<{ dataset: 'address' | 'location'; mode: 'full' | 'change'; files: string[] }> {
    const form = new FormData();
    form.set('file', file);
    const token = (globalThis as { __SIGNAGEONE_TOKEN__?: string }).__SIGNAGEONE_TOKEN__;
    const response = await fetch(this.mediaUrl('/api/v1/address-db/import'), {
      method: 'POST',
      body: form,
      headers: token ? { authorization: `Bearer ${token}` } : {},
      credentials: 'include',
    });
    const body = (await response.json().catch(() => null)) as
      | { dataset: 'address' | 'location'; mode: 'full' | 'change'; files: string[]; message?: string }
      | null;
    if (!response.ok || !body) {
      throw new ApiCallError(body?.message ?? `주소 DB 올리기 실패 (${response.status})`, response.status);
    }
    return body;
  }

  // ── 통합 관제 (다른 SignageOne 서버들 — 등록은 시스템 역할)
  listHubServers(): Promise<HubServerList> {
    return this.call('GET', '/api/v1/hub/servers');
  }
  /** 등록 — 서버가 적기 전에 그 서버에 닿는지·토큰이 통하는지 본다(아니면 400 과 이유). */
  createHubServer(input: HubServerInput): Promise<HubServerView> {
    return this.call('POST', '/api/v1/hub/servers', input);
  }
  /** 보낸 칸만 바꾼다. 토큰을 비워 두면 그대로 둔다. */
  updateHubServer(id: string, patch: Partial<HubServerInput>): Promise<HubServerView> {
    return this.call('PATCH', `/api/v1/hub/servers/${encodeURIComponent(id)}`, patch);
  }
  deleteHubServer(id: string): Promise<{ ok: true }> {
    return this.call('DELETE', `/api/v1/hub/servers/${encodeURIComponent(id)}`);
  }
  /** 지금 묻는다 — 토큰의 신원까지. */
  checkHubServer(id: string): Promise<HubServerView> {
    return this.call('POST', `/api/v1/hub/servers/${encodeURIComponent(id)}/check`);
  }
  hubDisplays(
    id: string,
    query: {
      status?: 'all' | 'online' | 'offline' | 'attention';
      q?: string;
      /** 그 서버의 위치(건물) 하나만. 빈 문자열은 위치 미지정. */
      location?: string;
      cursor?: string | null;
    } = {},
  ): Promise<HubDisplayPage> {
    const params = new URLSearchParams();
    if (query.status) params.set('status', query.status);
    if (query.q) params.set('q', query.q);
    if (query.location !== undefined) params.set('location', query.location);
    if (query.cursor) params.set('cursor', query.cursor);
    const qs = params.toString();
    return this.call('GET', `/api/v1/hub/servers/${encodeURIComponent(id)}/displays${qs ? `?${qs}` : ''}`);
  }
  /** 그 서버의 화면에 명령을 보낸다 — 판단은 그 서버가 하고 거절하면 그 말을 그대로 돌려준다. */
  hubCommand(serverId: string, displayId: string, kind: AgentCommandKind): Promise<{ ok: boolean; already?: boolean }> {
    return this.call(
      'POST',
      `/api/v1/hub/servers/${encodeURIComponent(serverId)}/displays/${encodeURIComponent(displayId)}/command`,
      { kind },
    );
  }

  // ── 플러그인 (시스템 역할 — `plugin:manage`)
  listPlugins(): Promise<PluginList> {
    return this.call('GET', '/api/v1/plugins');
  }
  pluginRuns(): Promise<{ items: PluginRunView[] }> {
    return this.call('GET', '/api/v1/plugins/runs');
  }
  /** 모듈의 단계·해제·배포를 정한다. 보낸 것만 바꾼다. */
  setModule(id: ModuleId, patch: Partial<ModuleFlags>): Promise<ModuleView> {
    return this.call('PATCH', `/api/v1/plugins/modules/${id}`, patch);
  }
  /** 고객사에 공개된 모듈만 — 관리자도 부를 수 있다(`read`). 공개하지 않은 것은 목록에 없다. */
  exposedModules(): Promise<{ modules: ExposedModule[] }> {
    return this.call('GET', '/api/v1/plugins/modules');
  }
  saveBackupExternal(input: Partial<Omit<BackupExternalSettings, 'last_scheduled_on'>>): Promise<BackupExternalSettings> {
    return this.call('PUT', '/api/v1/plugins/backup-external', input);
  }
  /** 경로 점검. 경로를 주지 않으면 저장된 경로를 본다. */
  checkBackupExternal(path?: string): Promise<PathInspection> {
    return this.call('POST', '/api/v1/plugins/backup-external/check', path === undefined ? {} : { path });
  }
  /** 지금 실행 — 기다리지 않고 실행 기록 id 를 받는다. 결과는 실행 기록에서 본다. */
  runBackupExternal(): Promise<{ run_id: string }> {
    return this.call('POST', '/api/v1/plugins/backup-external/run');
  }
  saveAlertNotify(input: AlertSettingsInput): Promise<AlertSettingsView> {
    return this.call('PUT', '/api/v1/plugins/alert-notify', input);
  }
  testAlertChannel(channel: AlertChannel): Promise<{ outcome: AlertNotificationView | null }> {
    return this.call('POST', '/api/v1/plugins/alert-notify/test', { channel });
  }

  // ── 감사·시스템
  /**
   * 내 대시보드 배치. **`cards: null` 은 "정한 적 없음"** 이고 빈 배열은 "전부 내림" 이다.
   */
  dashboardLayout(): Promise<{ cards: Array<{ id: string; span: number; height?: number | null }> | null }> {
    return this.call('GET', '/api/v1/me/dashboard');
  }
  saveDashboardLayout(
    cards: ReadonlyArray<{ id: string; span: number; height?: number | null }>,
  ): Promise<{ cards: Array<{ id: string; span: number; height?: number | null }> }> {
    return this.call('POST', '/api/v1/me/dashboard', { cards });
  }
  me(): Promise<Me> {
    return this.call('GET', '/api/v1/me');
  }

  auditLogs(
    params: {
      limit?: string;
      cursor?: string;
      action?: string;
      resource_type?: string;
      resource_id?: string;
      actor?: string;
      /** 기간 — 이 시각부터(포함)·이 시각 전까지(ISO). */
      from?: string;
      to?: string;
    } = {},
  ): Promise<ListResponse<AuditEntry>> {
    return this.call('GET', `/api/v1/audit-logs${query(params)}`);
  }
  /** 활동 로그 CSV 주소 — 조회와 같은 거르개. 내려받은 사실도 활동 로그에 남는다. */
  auditLogsCsvUrl(params: { resource_type?: string; actor?: string; from?: string; to?: string } = {}): string {
    return this.mediaUrl(`/api/v1/audit-logs.csv${query(params)}`);
  }

  /** `/health/ready` 는 인증이 없다 — 오케스트레이터가 토큰 없이 찔러야 하기 때문이다. */
  /**
   * 통합 API 의 계약.
   *
   * 인증이 필요 없는 경로지만 다른 호출과 같은 전송 계층으로 부른다 — iframe·호스트
   * 브리지 배포에서는 콘솔이 서버 주소를 직접 알지 못한다.
   */
  openApiSpec(): Promise<OpenApiDocument> {
    return this.call('GET', '/api/v1/public/openapi.json');
  }

  ready(): Promise<ReadyReport> {
    return this.call('GET', '/health/ready');
  }

  metrics(): Promise<MetricsReport> {
    return this.call('GET', '/health/metrics');
  }
}

function query(params: Record<string, string | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, value);
  }
  const serialized = search.toString();
  return serialized ? `?${serialized}` : '';
}

/** 서버가 정한 파일 이름. 없으면 날짜로 만든다 — 이름 없는 파일이 내려받기 폴더에 쌓이면 무엇인지 알 수 없다. */
export function backupFilename(contentDisposition: string | null): string {
  const star = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition ?? '');
  if (star?.[1]) return decodeURIComponent(star[1]);
  const plain = /filename="([^"]+)"/i.exec(contentDisposition ?? '');
  if (plain?.[1]) return plain[1];
  return `signageone-backup-${new Date().toISOString().slice(0, 10)}.tar.gz`;
}
